import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import {
  Sparkles, Wand2, Download, Film, Loader2, Hexagon, Image as ImageIcon,
  Upload, X, Shuffle, Lightbulb, Users, UsersRound, LayoutGrid, RotateCw, Package,
  ArrowRight, ArrowLeft, Check, RefreshCw, Play, Pause, Volume2, VolumeX, ChevronRight, ChevronDown, Zap, Send, Trash2, GitCompareArrows
} from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import JSZip from "jszip";
import { STYLES, STYLE_GROUPS, PROMPT_LIBRARY, styleLabel, BIO_TONES } from "@/lib/styles";
import { MotionPresetsPicker, resolveMotionPreset } from "@/components/studio/MotionPresets";
import { AspectRatioCard } from "@/components/studio/AspectRatioCard";
import { OmniReferenceCard } from "@/components/studio/OmniReferenceCard";
import { ImageRefSlots } from "@/components/studio/ImageRefSlots";
import { VoiceCloneModal } from "@/components/studio/VoiceCloneModal";
import { PipelineHistoryStrip } from "@/components/studio/PipelineHistoryStrip";
import { BioCard } from "@/components/studio/BioCard";
import { SoundControl, useVideoVolume } from "@/components/studio/SoundControl";
import { StartEndFramePicker } from "@/components/studio/StartEndFramePicker";
import { AmbientBg, ToolChip, Toolbar, GlowCard } from "@/components/studio/atoms";
import { ChatPromptDock } from "@/components/studio/ChatPromptDock";
import { GenerationFeed } from "@/components/studio/GenerationFeed";
import { QueueProgressPill } from "@/components/studio/QueueProgressPill";
import { StylePresetChip } from "@/components/studio/StylePresetChip";
import { StyleBlendPreview } from "@/components/studio/StyleBlendPreview";
import { BeforeAfterSlider, BeforeAfterSliderModal } from "@/components/studio/BeforeAfterSlider";
import { MultiframeRail } from "@/components/studio/MultiframeRail";
import { SubjectLockPill } from "@/components/studio/SubjectLockPill";
import { VideoReadyPopup } from "@/components/studio/VideoReadyPopup";
import { ImageDetailDialog } from "@/components/studio/ImageDetailDialog";
import { NewResultsPill, useNewResultsSignal } from "@/components/studio/NewResultsPill";
import { useGenerationQueue } from "@/hooks/useGenerationQueue";
import { useStudioSubjectLock } from "@/hooks/useStudioSubjectLock";
import { useDevMode } from "@/context/DevModeContext";
import {
  MOSAIC_IMAGES,
  ASPECTS,
  RESOLUTIONS,
  VIDEO_RESOLUTIONS,
  MODES,
  PIPELINE_STEPS,
  readHistory,
  writeHistory,
  TURN_VIEWS,
  TURN_LABELS,
  SHEET_ROWS,
} from "@/pages/studio/constants";

export default function Studio() {
  const { devMode } = useDevMode();
  // Dev-only experimental style presets fetched from /api/dev/style-presets
  // when Dev Mode is ON. Cached in-component; hidden entirely otherwise.
  const [devStylePresets, setDevStylePresets] = useState([]);
  useEffect(() => {
    if (!devMode) { setDevStylePresets([]); return; }
    let cancelled = false;
    api.get("/dev/style-presets").then(({ data }) => {
      if (!cancelled) setDevStylePresets(data.presets || []);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [devMode]);

  const [mode, setMode] = useState("turnaround");
  const [prompt, setPrompt] = useState("");
  // Multi-select style presets — array of style ids. Empty = no style modifier applied.
  // Persisted across page navigations so the user's spell-card selection survives.
  const [stylesArr, setStylesArr] = useState(() => {
    try { const raw = sessionStorage.getItem("geneo:styles"); return raw ? JSON.parse(raw) : []; } catch { return []; }
  });
  // Persist on every change so cross-page nav keeps the picks
  useEffect(() => {
    try { sessionStorage.setItem("geneo:styles", JSON.stringify(stylesArr)); } catch {}
  }, [stylesArr]);
  const toggleStyle = (id) => setStylesArr((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  const clearStyles = () => setStylesArr([]);
  // Derived legacy vars (kept for backward-compat with feed captures / remix / uploads)
  const style  = stylesArr[0] || null;
  const styleB = stylesArr[1] || null;
  const blend  = 50;
  const blendOn = stylesArr.length >= 2;
  const [aspect, setAspect] = useState("3:4");
  const [resolution, setResolution] = useState("FHD");
  const [videoResolution, setVideoResolution] = useState("720p");
  const [omniRefs, setOmniRefs] = useState([]); // [{url, file, uploaded_url}]
  // Cast Library: up to 5 pinned characters. The first entry is the active
  // cast — auto-attached as a subject-lock reference to every new generation.
  const [castLibrary, setCastLibrary] = useState([]);
  const pinnedCharacter = castLibrary[0] || null;
  // Ensemble / Trio Mode: array of cast_generation_ids of co-stars (max 2).
  // 1 partner = Ensemble (duo), 2 partners = Trio (team-up / showdown + bystander).
  const MAX_ENSEMBLE_PARTNERS = 2;
  const [ensembleIds, setEnsembleIds] = useState([]);
  const ensembleCharacters = ensembleIds
    .map((id) => castLibrary.find((c) => c.character_generation_id === id))
    .filter((c) => c && c.character_generation_id !== pinnedCharacter?.character_generation_id);
  // Back-compat alias for any legacy read of `ensembleCharacter` (first partner).
  const ensembleCharacter = ensembleCharacters[0] || null;
  const [pinningCharacter, setPinningCharacter] = useState(false);
  const [castMenuOpen, setCastMenuOpen] = useState(false);
  // Consistency scores: { [generation_id]: {score, components, loading?} }
  // Fetched lazily as each new render lands in the feed, only when a cast is active.
  const [consistencyScores, setConsistencyScores] = useState({});
  // Start & End frame anchors for video generation (T2V, I2V, V2V)
  const [startFrame, setStartFrame] = useState(null); // {url, generation_id}
  const [endFrame, setEndFrame] = useState(null);     // {url, generation_id}
  const [motionPrompt, setMotionPrompt] = useState("");
  const [motionPreset, setMotionPreset] = useState(null); // id from MOTION_PRESETS or null
  const [duration, setDuration] = useState(5);
  const [lockCharacter, setLockCharacter] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [animating, setAnimating] = useState(false);
  const [current, setCurrent] = useState(null);
  const [uploadPreview, setUploadPreview] = useState(null);
  const [turnRefPreview, setTurnRefPreview] = useState(null);
  const [exportingSheet, setExportingSheet] = useState(false);
  const [exportingZip, setExportingZip] = useState(false);
  const [spinning, setSpinning] = useState(false);
  const [reelBuilding, setReelBuilding] = useState(false);
  const [storyReel, setStoryReel] = useState(null); // { generation_id, video_url, ... }
  const [editPrompt, setEditPrompt] = useState("");
  const [editing, setEditing] = useState(false);
  const [history, setHistory] = useState(readHistory());
  const [historySelectMode, setHistorySelectMode] = useState(false);
  const [historySelected, setHistorySelected] = useState(new Set());
  const [promptAssisting, setPromptAssisting] = useState(false);
  const [soundOn, setSoundOn] = useState(true);
  const [volume, setVolume] = useState(0.7);
  const stageVideoRef = useRef(null);
  const turntableVideoRef = useRef(null);
  const storyReelVideoRef = useRef(null);
  useVideoVolume(stageVideoRef, volume, soundOn);
  useVideoVolume(turntableVideoRef, volume, soundOn);
  useVideoVolume(storyReelVideoRef, volume, soundOn);
  const toggleSound = () => setSoundOn((s) => !s);
  const handleVolume = (v) => {
    setVolume(v);
    if (v === 0 && soundOn) setSoundOn(false);
    if (v > 0 && !soundOn) setSoundOn(true);
  };

  // NEW state — edit chain, bio tone, audio narration
  const [editChain, setEditChain] = useState([]); // [{generation_id, image_url, edit_instruction, prompt}]
  const [compareOpen, setCompareOpen] = useState(false);
  // Active camera-angle (image mode) and camera-move (video mode) — surfaced in the AUTO ON status pill
  const [activeAngle, setActiveAngle] = useState(null);
  const [activeCameraMove, setActiveCameraMove] = useState(null);
  const [bioTone, setBioTone] = useState("mythic");
  const [bioRerolling, setBioRerolling] = useState(false);
  const [voice, setVoice] = useState("rachel");
  const [voices, setVoices] = useState([]); // filled from /api/tts/voices
  const [ttsLoading, setTtsLoading] = useState(false);
  const [audioSrc, setAudioSrc] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);

  const fileRef = useRef(null);
  const turnRefFileRef = useRef(null);
  const videoRef = useRef(null);
  const voiceSampleRef = useRef(null);

  // V2V state
  const [videoPreview, setVideoPreview] = useState(null); // { url, file, uploaded_url? }
  const [uploadingVideo, setUploadingVideo] = useState(false);

  // Voice cloning modal state
  const [voiceCloneOpen, setVoiceCloneOpen] = useState(false);
  const [voiceCloning, setVoiceCloning] = useState(false);
  const [voiceCloneName, setVoiceCloneName] = useState("");

  // Dock + feed (fire-and-forget parallel generation queue)
  const [dockMode, setDockMode] = useState("image");   // image | video | v2v | turnaround
  const [videoSubmode, setVideoSubmode] = useState("omni"); // omni | endpoints | multiframe | smartedit | long
  const [videoModel, setVideoModel] = useState("2.0");        // "2.0" | "2.5" — Seedance model. 2.5 is a flat 10 credits.
  const [musicEnabled, setMusicEnabled] = useState(true);     // MUSIC ON/OFF toggle — when OFF, suppress background music (SFX stay)
  const [multiframes, setMultiframes] = useState([null, null, null, null]); // 4-slot keyframe rail for Multiframes submode

  // ── Character/Object Consistency (Session Subject Lock) ─────────────────
  // Extracted to `useStudioSubjectLock` hook. See hooks/useStudioSubjectLock.js
  // for the sessionStorage-backed lock + cleared-flag + rearm semantics.
  const {
    subjectLock,
    subjectLockCleared,
    setSubjectLock,
    clearSubjectLock,
    rearmSubjectLock,
  } = useStudioSubjectLock();
  const [autoOn, setAutoOn] = useState(true);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const feed = useGenerationQueue();

  // On Studio mount, pull completed async jobs from the last 24h that
  // aren't already in the local feed. This covers the case where a job
  // finished while the tab was closed / backgrounded, so results are never
  // "lost" to a page refresh — they land in the feed on next load.
  useEffect(() => {
    feed.reconcileFromServer?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Video ready popup ──────────────────────────────────────────────────
  // Fires the moment ANY video-kind feed item transitions to `done`. This
  // is the belt-and-suspenders UX: even if the feed insertion path glitches
  // (tab was backgrounded, network hiccup mid-poll, etc.), the user gets an
  // in-your-face notification with a manual "SEND TO FEED" button that
  // re-injects the item. Also covers image→video quick actions completing
  // from off-screen.
  const [videoReady, setVideoReady] = useState(null);
  const notifiedVideoIdsRef = useRef(new Set());
  useEffect(() => {
    // Find the newest video item that just flipped to done AND we haven't
    // shown the popup for yet.
    const fresh = [...feed.items].reverse().find((it) => (
      it.status === "done" &&
      it.kind === "video" &&
      it.media_url &&
      !notifiedVideoIdsRef.current.has(it.id)
    ));
    if (!fresh) return;
    notifiedVideoIdsRef.current.add(fresh.id);
    setVideoReady(fresh);
    // Also mirror to Gallery-visible generations by broadcasting a refresh
    // event so any open Gallery tab pulls the new item without a page reload.
    try { window.dispatchEvent(new CustomEvent("gallery:refresh")); } catch {}
  }, [feed.items]);

  // Manual "SEND TO FEED" — guarantees the popup's item exists in the feed
  // state. Idempotent: if the same id is already present, we just scroll to
  // it; otherwise we re-inject a done card so it renders like every other.
  const sendVideoResultToFeed = useCallback((item) => {
    if (!item) return;
    feed.setItems?.((prev) => {
      const already = prev.find((it) => it.id === item.id);
      if (already) return prev;
      return [...prev, { ...item, status: "done" }];
    });
    // Scroll into view on the next tick so the DOM has time to mount it.
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="feed-item-${item.id}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 120);
    toast.success("Video pushed to feed.");
  }, [feed]);

  // ── Auto-scroll on completion ──────────────────────────────────────────
  // When a fresh generation lands (transitions to `done` or `error`), gently
  // center it in the viewport so the user never has to hunt for their new
  // card. If the user is scrolled UP (e.g. reviewing older results), we
  // skip the yank — the `NewResultsPill` floats at the top so they know to
  // jump down when they're ready. Seeds the seen-set on first render with
  // everything already hydrated from localStorage so page refresh never
  // yanks scroll; only genuine LIVE completions do.
  const seenTerminalIdsRef = useRef(null);
  useEffect(() => {
    if (seenTerminalIdsRef.current === null) {
      const seed = new Set(
        feed.items
          .filter((it) => it.status === "done" || it.status === "error")
          .map((it) => it.id),
      );
      seenTerminalIdsRef.current = seed;
      return;
    }
    const seen = seenTerminalIdsRef.current;
    const fresh = [...feed.items].reverse().find((it) => (
      (it.status === "done" || it.status === "error") && !seen.has(it.id)
    ));
    if (!fresh) return;
    seen.add(fresh.id);
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="feed-item-${fresh.id}"]`);
      if (!el) return;
      // Only auto-scroll if the user is already close to the bottom (feed
      // tail) — i.e. actively watching the latest cards land. If they've
      // scrolled up to review history, don't rip the viewport out from
      // under them; the NewResultsPill will handle the "jump to newest".
      const scrollNearBottom =
        window.innerHeight + window.scrollY >= document.body.scrollHeight - 400;
      if (scrollNearBottom) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 140);
  }, [feed.items]);

  // ── Fresh-results pill signal ──────────────────────────────────────────
  // Shows a cyan "N NEW RESULTS READY" pill at the top of the viewport when
  // a fresh render lands while the user is scrolled up. Auto-hides when
  // the newest card re-enters the viewport.
  const newResults = useNewResultsSignal(feed.items);

  // Watch the feed: whenever a new done render lands AND a cast is active,
  // fetch its consistency score against the pinned character. Cached server-side
  // so repeat visits are cheap. Skips items that already have a score in memory.
  useEffect(() => {
    if (!pinnedCharacter?.character_generation_id) return;
    const activeGenId = pinnedCharacter.character_generation_id;
    const targets = feed.items.filter((it) => (
      it.status === "done" &&
      it.media_url &&
      it.kind === "image" &&
      it.generation_id &&
      it.generation_id !== activeGenId &&
      !(it.generation_id in consistencyScores)
    ));
    if (!targets.length) return;
    // Debounced serial fetch — no burst on initial hydration
    let cancelled = false;
    (async () => {
      for (const it of targets.slice(0, 12)) {
        if (cancelled) return;
        try {
          const { data } = await api.post("/character/consistency", { generation_id: it.generation_id });
          if (!cancelled) {
            setConsistencyScores((prev) => ({
              ...prev,
              [it.generation_id]: { score: data.score, components: data.components },
            }));
          }
        } catch {
          if (!cancelled) {
            setConsistencyScores((prev) => ({ ...prev, [it.generation_id]: { score: null } }));
          }
        }
      }
    })();
    return () => { cancelled = true; };
    // consistencyScores is intentionally omitted — including it would loop
    // the effect on every score write. We filter targets from the current
    // snapshot at effect entry, which is the desired freshness window.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feed.items, pinnedCharacter?.character_generation_id]);

  // Compose the current params snapshot for the queue runner
  const buildDockParams = () => {
    const ref_urls = buildRefUrls();
    // Multiframes submode: fold keyframe urls into reference_urls in slot order
    const mframe_urls = (videoSubmode === "multiframe" && dockMode === "video")
      ? multiframes.map((s) => s?.uploaded_url).filter(Boolean)
      : [];
    // Session subject lock is prepended so identity anchors every generation.
    // Supports multi-subject locks (`uploaded_urls[]`) — all are sent as
    // references so Omni can blend the whole cast. Falls back to the legacy
    // single `uploaded_url` for older lock snapshots.
    // When the user toggles the lock OFF (via SubjectLockPill), we skip
    // sending any URLs but leave the attachment intact so they can re-enable
    // without re-uploading.
    const lockUrls = Array.isArray(subjectLock?.uploaded_urls) && subjectLock.uploaded_urls.length
      ? subjectLock.uploaded_urls
      : (subjectLock?.uploaded_url ? [subjectLock.uploaded_url] : []);
    const lockActive = lockUrls.length > 0 && subjectLock?.enabled !== false;
    const lock_url = lockActive ? lockUrls : [];
    // Dedup while preserving order
    const combined_refs = Array.from(new Set([...lock_url, ...mframe_urls, ...ref_urls]));
    // Style params: single-style → send `style` (backend injects rich STYLE_PROMPTS descriptor)
    // Multi-style (2+) → omit style params; equal-weight blend is injected via prompt suffix.
    const styleParams = stylesArr.length === 1
      ? { style: stylesArr[0] }
      : {};
    return {
      aspect_ratio: aspect,
      resolution,
      video_resolution: videoResolution,
      model_version: videoModel,
      music_enabled: musicEnabled,
      ...styleParams,
      reference_urls: combined_refs.length ? combined_refs : undefined,
      // With Multiframes we anchor the first keyframe as start and last as end for cinematic pacing
      start_image_url: (videoSubmode === "multiframe" && mframe_urls[0]) || startFrame?.url || undefined,
      end_image_url:   (videoSubmode === "multiframe" && mframe_urls[mframe_urls.length - 1]) || endFrame?.url || undefined,
      video_url: videoPreview?.uploaded_url || undefined,
    };
  };

  // Auto-lock the subject on the FIRST successful generation of the session.
  // Later successes don't override — only the user can (manual clear or new upload).
  // Skip while `subjectLockCleared` is set — the user explicitly opted out until they act again.
  useEffect(() => {
    if (subjectLock || subjectLockCleared) return;
    const firstDone = feed.items.find((it) => it.status === "done" && it.media_url && it.kind === "image");
    if (firstDone) {
      setSubjectLock({
        url: firstDone.media_url,
        uploaded_url: firstDone.media_url,   // generation media_url is already object-storage backed
        uploaded_urls: [firstDone.media_url],
        source: "generation",
        prompt: firstDone.prompt,
        locked_at: new Date().toISOString(),
      });
      toast.success("Subject locked — identity will carry across generations.");
    }
  }, [feed.items, subjectLock, subjectLockCleared, setSubjectLock]);

  // Auto-lock on the first uploaded Omni reference too — captures the user's
  // intent when they seed an existing character/object image.
  useEffect(() => {
    if (subjectLock || subjectLockCleared) return;
    const firstUploaded = omniRefs.find((r) => r.uploaded_url);
    if (firstUploaded) {
      setSubjectLock({
        url: firstUploaded.url || firstUploaded.uploaded_url,
        uploaded_url: firstUploaded.uploaded_url,
        uploaded_urls: [firstUploaded.uploaded_url],
        source: "upload",
        prompt: firstUploaded.name || null,
        locked_at: new Date().toISOString(),
      });
      toast.success("Subject locked from upload — identity will carry across generations.");
    }
  }, [omniRefs, subjectLock, subjectLockCleared, setSubjectLock]);

  // Dock submit — routes to the queue with current params
  const handleDockSubmit = async ({ prompt: promptText, mode: dm }) => {
    rearmSubjectLock();
    const params = buildDockParams();
    // Character/object consistency: append a subtle identity-preservation guardrail
    // to the prompt whenever a subject lock is active. Models honor references best
    // when the prompt explicitly asks for identity/outfit preservation.
    const suffix = styleSuffix();
    // Ensemble / Trio co-star phrase — injected when 1+ ensemble partner is
    // active so the model composes every hero into the same scene while
    // honoring their reference images already added to ref_urls.
    let ensemblePhrase = "";
    if (pinnedCharacter && ensembleCharacters.length > 0) {
      const names = [pinnedCharacter.name || "Lead", ...ensembleCharacters.map((c) => c.name || "Co-star")];
      const modeTag = ensembleCharacters.length === 1 ? "Ensemble scene" : "Trio scene";
      const joined = names.length === 2
        ? `${names[0]} and ${names[1]}`
        : `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`;
      ensemblePhrase = `\n\n[${modeTag}: ${joined} appear together in the same shot — preserve every character's face, skin tone, outfit, accessories, and identity faithfully from their reference images.]`;
    }
    const basePrompt = subjectLock
      ? `${promptText}\n\n[Identity lock: preserve the exact character, face, skin tone, outfit, accessories, colors, and fine details from the reference image. Only change the pose, action, and environment described above.]`
      : promptText;
    const promptWithLock = (suffix ? basePrompt + suffix : basePrompt) + ensemblePhrase;
    // Auto: if user set aspect to a non-video ratio while in video mode, coerce to 16:9
    if ((dm === "video" || dm === "v2v") && !["16:9", "9:16", "1:1"].includes(params.aspect_ratio)) {
      params.aspect_ratio = "16:9";
    }
    if (dm === "v2v" && !params.video_url) {
      toast.error("Attach a source clip first (Attach → Source Clip).");
      return;
    }
    setPrompt("");   // clear composer after queueing
    // Store the human-readable prompt in the feed but send the identity-augmented one to the model
    await feed.enqueue({
      mode: dm,
      prompt: promptText,
      params: { ...params, prompt_augmented: promptWithLock },
    });
  };

  const handleFeedEdit = (item) => { setPrompt(item.prompt || ""); setDockMode(item.mode); toast.success("Prompt pulled into composer"); };
  const handleFeedRegenerate = (item) => feed.regenerate(item);

  // Retry a failed generation — re-enqueues the exact same mode + prompt +
  // params so users don't have to re-type the prompt or re-attach refs. If
  // the failed job already refunded the original spend (fal.ai billing
  // failures do this automatically), the retry pays fresh from the restored
  // balance — so no double-charge ever occurs. If the failure did NOT
  // refund (rare — non-billing errors), the retry uses the user's current
  // balance normally.
  const handleFeedRetry = async (item) => {
    if (!item || item.status !== "error") return;
    // Recovery-first path: if the failed job has a `job_id`, try the
    // server's /recover endpoint. When fal.ai successfully produced a
    // result but a downstream step (storage, DB) crashed, the raw URL
    // was stashed on the job doc — recovery flips it to `completed`
    // and returns the result WITHOUT charging credits again.
    if (item.job_id) {
      try {
        const { data } = await api.post(`/jobs/${item.job_id}/recover`);
        const result = data?.result || {};
        if (result.video_url || result.image_url) {
          const extras = result.video_url
            ? { media_url: result.video_url, kind: "video" }
            : { media_url: result.image_url, kind: "image" };
          feed.setItems((prev) => prev.map((it) => it.id === item.id ? {
            ...it,
            status: "done",
            error: null,
            refunded: 0,
            generation_id: result.generation_id || it.generation_id,
            ...extras,
          } : it));
          toast.success("Video recovered — no credits charged.");
          return;
        }
      } catch (e) {
        // 404 means nothing to recover — fall through to a fresh re-run.
      }
    }
    // Remove the failed card so the retry's optimistic card takes its slot,
    // keeping the feed chronologically clean.
    try { feed.remove(item.id); } catch {}
    feed.enqueue({ mode: item.mode, prompt: item.prompt, params: item.params || {} });
    toast.success(item.refunded ? `Retrying — original ${item.refunded} credits are back in your wallet.` : "Retrying generation…");
  };

  // Studio feed detail state — focused image detail dialog (new, replaces the
  // old "scroll to Advanced Editor with style presets" flow).
  const [detailItem, setDetailItem] = useState(null);

  // Open a completed feed item. For images: show the focused detail dialog
  // (image + prompt + Download / Private / Regenerate / Create New).
  // For videos & turnarounds: load into the classic editor (unchanged flow).
  const handleFeedOpenDetail = (item) => {
    if (!item.media_url && !item.views) return;
    // Image path → direct detail dialog, skip the style-presets Advanced Editor.
    if (item.kind === "image" && item.media_url && item.mode !== "turnaround") {
      setDetailItem(item);
      return;
    }
    const currentPayload = {
      generation_id: item.generation_id,
      prompt: item.prompt,
      image_url: item.kind === "image" ? item.media_url : undefined,
      video_url: item.kind === "video" ? item.media_url : undefined,
      views: item.views,   // turnaround
      type: item.mode === "turnaround" ? "turnaround" : (item.kind === "video" ? "video" : "image"),
    };
    setCurrent(currentPayload);
    // Restart the edit chain with this frame as the seed
    if (item.kind === "image") {
      setEditChain([{
        generation_id: item.generation_id,
        image_url: item.media_url,
        prompt: item.prompt,
        edit_instruction: null,
      }]);
    }
    // Sync classic-editor mode
    if (item.mode === "video")           switchMode("text-video");
    else if (item.mode === "v2v")        switchMode("video-to-video");
    else if (item.mode === "turnaround") switchMode("turnaround");
    else                                  switchMode("pipeline"); // image → pipeline gives edit + animate
    // Expand advanced editor and scroll to it
    setShowAdvanced(true);
    requestAnimationFrame(() => {
      const el = document.querySelector('[data-testid="advanced-editor-region"]');
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    toast.success("Loaded into the editor — ready to refine.");
  };

  // "Create New Image" from the detail dialog — scroll to the prompt dock
  // and focus the composer so the user can start a fresh generation.
  const handleCreateNewFromDetail = () => {
    setPrompt("");
    setDockMode("create");
    requestAnimationFrame(() => {
      const el = document.querySelector('[data-testid="dock-prompt-input"]')
             || document.querySelector('[data-testid="chat-prompt-dock"]');
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        if (el.tagName === "TEXTAREA" || el.tagName === "INPUT") el.focus();
      }
    });
  };

  const styleName = (id) => STYLES.find((s) => s.id === id)?.name || id;
  // Single style: send `style` id → backend injects the rich STYLE_PROMPTS descriptor.
  // Multi-style (2+): equal-weight blend expressed through a prompt suffix instead of style param.
  const stylePayload = () => stylesArr.length === 1 ? { style: stylesArr[0] } : {};
  // Prompt suffix injected when 2+ spell cards are active — equal-weight fusion phrase.
  const styleSuffix = () => {
    if (stylesArr.length < 2) return "";
    const names = stylesArr.map((id) => styleLabel(id));
    const last = names[names.length - 1];
    const rest = names.slice(0, -1).join(", ");
    return ` · Styled equally with: ${rest}${names.length > 2 ? "," : ""} and ${last} aesthetics — fuse all styles cohesively with no single one dominating.`;
  };
  const withStyleSuffix = (p) => (p || "") + styleSuffix();

  const location = useLocation();
  const navigate = useNavigate();

  // Load voices once
  useEffect(() => {
    api.get("/tts/voices").then((r) => setVoices(r.data?.voices || [])).catch(() => {});
  }, []);

  // Auto-record pipeline routings
  useEffect(() => {
    if (!current?.generation_id) return;
    pushToHistory(current);
    // pull cached bio audio if present from server response
    if (current.bio_audio_url && current.bio_audio_voice) {
      setAudioSrc(current.bio_audio_url);
      setVoice(current.bio_audio_voice);
    } else {
      setAudioSrc(null);
      setIsPlaying(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.generation_id, current?.video_url, current?.turntable_video_url, current?.bio_audio_url]);

  // Handle remix pre-fill from Gallery
  useEffect(() => {
    // Cross-page dock handoff: Storyboard/Avatar composer submitted → we resume here
    try {
      const raw = sessionStorage.getItem("geneo:dockHandoff");
      if (raw) {
        const { prompt: p, mode: m } = JSON.parse(raw);
        sessionStorage.removeItem("geneo:dockHandoff");
        if (p) setPrompt(p);
        if (m) setDockMode(m);
      }
    } catch {}
    const r = location.state?.remix;
    const pipe = location.state?.pipelineRoute;
    // Frame-capture handoff — the Timeline Editor's "Use as Reference" flow
    // routes here with `attachRef: {generation_id, image_url, source}`.
    // Push it straight into the omni-reference rail so the user lands in
    // Studio with the frame already attached and ready to prompt against.
    const attachRef = location.state?.attachRef;
    // Batch variant — from the frame-series "Attach All" bulk action.
    // Pushes multiple frames at once, respecting the same 5-cap and dedupe rules.
    const attachRefBatch = location.state?.attachRefBatch;
    if (Array.isArray(attachRefBatch) && attachRefBatch.length) {
      let added = 0;
      let capped = false;
      setOmniRefs((prev) => {
        const next = prev.slice();
        for (const ref of attachRefBatch) {
          if (!ref?.image_url) continue;
          if (next.some((x) => x.uploaded_url === ref.image_url)) continue;
          if (next.length >= 5) { capped = true; break; }
          next.push({
            url: ref.image_url,
            uploaded_url: ref.image_url,
            generation_id: ref.generation_id,
            kind: "image",
            source: ref.source || "editor-series-batch",
          });
          added++;
        }
        return next;
      });
      if (added > 0) toast.success(`${added} frame${added === 1 ? "" : "s"} attached to reference rail.`);
      if (capped) toast.warning("Reference rail capped at 5 — some frames skipped.");
      navigate(location.pathname, { replace: true, state: null });
      return;
    }
    if (attachRef && attachRef.image_url) {
      let added = false;
      setOmniRefs((prev) => {
        // Dedupe by image_url so a double-nav (React 18 StrictMode re-run
        // or a fast round-trip) doesn't stack the same frame twice.
        if (prev.some((x) => x.uploaded_url === attachRef.image_url)) return prev;
        // Cap at 5 to match the rail's `refsCap`.
        if (prev.length >= 5) {
          toast.error("Reference rail is full (5). Remove one to attach this frame.");
          return prev;
        }
        added = true;
        return [
          ...prev,
          {
            url: attachRef.image_url,
            uploaded_url: attachRef.image_url,
            generation_id: attachRef.generation_id,
            kind: "image",
            source: attachRef.source || "editor-frame-capture",
          },
        ];
      });
      if (added) toast.success("Frame attached to reference rail.");
      navigate(location.pathname, { replace: true, state: null });
      return;
    }
    if (pipe && pipe.generation_id) {
      // Route a gallery image directly into the pipeline
      setCurrent(pipe);
      setEditChain([{
        generation_id: pipe.generation_id,
        image_url: pipe.image_url,
        prompt: pipe.prompt,
        edit_instruction: null,
      }]);
      setMode("pipeline");
      toast.success("Loaded into the pipeline — ready to edit.");
      navigate(location.pathname, { replace: true, state: null });
      return;
    }
    if (!r) return;
    if (r.style || r.style_b) {
      const arr = [];
      if (r.style) arr.push(r.style);
      if (r.style_b && r.style_b !== r.style) arr.push(r.style_b);
      setStylesArr(arr);
    }
    if (r.prompt) setPrompt(r.prompt);
    setMode("create");
    toast.success("Remix loaded — tweak and create.");
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, location.pathname, navigate]);

  const chosenStyle = STYLES.find((s) => s.id === style);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file."); return; }
    if (file.size > 15 * 1024 * 1024) { toast.error("Max size is 15MB."); return; }
    setUploadPreview({ url: URL.createObjectURL(file), file });
  };

  const handleTurnRefFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file."); return; }
    if (file.size > 15 * 1024 * 1024) { toast.error("Max size is 15MB."); return; }
    setTurnRefPreview({ url: URL.createObjectURL(file), file });
  };

  const assistPrompt = async () => {
    setPromptAssisting(true);
    try {
      const { data } = await api.post("/prompt/assist", { prompt, style });
      if (data?.prompt) {
        setPrompt(data.prompt);
        toast.success("Prompt supercharged.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Prompt Assist temporarily unavailable");
    } finally {
      setPromptAssisting(false);
    }
  };

  const assistEditPrompt = async () => {
    setPromptAssisting(true);
    try {
      const { data } = await api.post("/prompt/assist", { prompt: editPrompt, style });
      if (data?.prompt) {
        setEditPrompt(data.prompt);
        toast.success("Edit prompt supercharged.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Prompt Assist temporarily unavailable");
    } finally {
      setPromptAssisting(false);
    }
  };

  const generateImage = async () => {
    if (!prompt.trim()) { toast.error("Speak your vision first."); return; }
    setGenerating(true);
    try {
      const ref_urls = buildRefUrls();
      const { data } = await api.post("/generate/image", {
        prompt: withStyleSuffix(prompt),
        aspect_ratio: aspect,
        resolution,
        reference_urls: ref_urls.length ? ref_urls : undefined,
        ...stylePayload(),
      });
      setCurrent(data);
      setEditChain([{
        generation_id: data.generation_id,
        image_url: data.image_url,
        prompt: data.prompt,
        edit_instruction: null,
      }]);
      toast.success(ref_urls.length ? `Vision created with ${ref_urls.length} refs.` : "Vision created — ready to route.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const addOmniRef = async (file) => {
    if (!file) return;
    // Rail cap is 5 per the Feb 2026 redesign; the paperclip fallback and drag-drop share this ceiling.
    if (omniRefs.length >= 5) { toast.error("Image references are capped at 5."); return; }
    const isImg = file.type.startsWith("image/");
    const isVid = file.type.startsWith("video/");
    if (!isImg && !isVid) { toast.error("Only images and videos supported."); return; }
    if (file.size > 15 * 1024 * 1024) { toast.error("Max size per file is 15MB."); return; }
    const localUrl = URL.createObjectURL(file);
    const tmp = { url: localUrl, file, uploaded_url: null, kind: isVid ? "video" : "image" };
    setOmniRefs((prev) => [...prev, tmp]);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("style", style);
      const endpoint = isVid ? "/upload/video" : "/upload/image";
      const { data } = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const uploaded_url = data.image_url || data.video_url;
      setOmniRefs((prev) => prev.map((r) => (r.file === file ? { ...r, uploaded_url } : r)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
      setOmniRefs((prev) => prev.filter((r) => r.file !== file));
    }
  };

  const removeOmniRef = (idx) => setOmniRefs((prev) => prev.filter((_, i) => i !== idx));

  // Swap two reference slots — used by the rail's drag-drop-to-swap flow.
  // Immutable swap so React re-renders both thumbs; bail if either index is
  // out of range or they point at the same slot.
  const reorderOmniRef = (fromIdx, toIdx) => {
    setOmniRefs((prev) => {
      if (
        fromIdx === toIdx ||
        fromIdx < 0 || toIdx < 0 ||
        fromIdx >= prev.length || toIdx >= prev.length
      ) return prev;
      const next = prev.slice();
      [next[fromIdx], next[toIdx]] = [next[toIdx], next[fromIdx]];
      return next;
    });
  };

  // Replace a single reference slot (used when the user drops an external
  // file onto an *occupied* thumbnail — the slot's contents are swapped
  // out for the new upload). Preserves slot position and other thumbs.
  const replaceOmniRef = async (idx, file) => {
    if (!file) return;
    const isImg = file.type?.startsWith("image/");
    const isVid = file.type?.startsWith("video/");
    if (!isImg && !isVid) { toast.error("Only image / video files can be dropped onto a slot."); return; }
    if (file.size > 15 * 1024 * 1024) { toast.error("Max size per file is 15MB."); return; }
    const localUrl = URL.createObjectURL(file);
    setOmniRefs((prev) => {
      if (idx < 0 || idx >= prev.length) return prev;
      const next = prev.slice();
      next[idx] = { url: localUrl, file, uploaded_url: null, kind: isVid ? "video" : "image" };
      return next;
    });
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("style", style);
      const endpoint = isVid ? "/upload/video" : "/upload/image";
      const { data } = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const uploaded_url = data.image_url || data.video_url;
      setOmniRefs((prev) => prev.map((r, i) => (i === idx && r.file === file ? { ...r, uploaded_url } : r)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reference upload failed");
      // Revert to keep the UI honest — drop only the specific failed slot.
      // Match by (index AND file identity) so we never accidentally remove
      // a different slot the user filled while the upload was in flight.
      setOmniRefs((prev) => prev.filter((r, i) => !(i === idx && r?.file === file)));
    }
  };

  // Feed → attach handler: user dropped a completed render onto the paperclip
  // (or clicked "Use as reference"). Route by current mode.
  const attachFeedItem = (feedItem) => {
    if (!feedItem?.media_url) { toast.error("This render has no media yet."); return; }
    const isVid = feedItem.kind === "video";
    if (dockMode === "image") {
      if (isVid) { toast.error("Switch to video mode to reference a video render."); return; }
      if (omniRefs.length >= 5) { toast.error("Image references are capped at 5."); return; }
      // Image URL is already hosted — no re-upload needed.
      setOmniRefs((prev) => [
        ...prev,
        { url: feedItem.media_url, uploaded_url: feedItem.media_url, kind: "image", feed_id: feedItem.id },
      ]);
      toast.success("Reference attached from feed");
      return;
    }
    // Video / animation / turnaround / v2v modes
    if (dockMode === "v2v" || videoSubmode === "smartedit") {
      if (!isVid) { toast.error("V2V needs a video source. Drop a video render."); return; }
      setVideoPreview({ url: feedItem.media_url, uploaded_url: feedItem.media_url, file: null });
      toast.success("V2V source set from feed");
      return;
    }
    if (dockMode === "video" || dockMode === "turnaround") {
      if (isVid) {
        // In text-to-video, a video render becomes the source clip (v2v route)
        setVideoPreview({ url: feedItem.media_url, uploaded_url: feedItem.media_url, file: null });
        toast.success("Clip attached — switch to V2V to restyle it");
        return;
      }
      // Image → first-frame anchor for video, or omni reference if slot free
      if (!startFrame) {
        setStartFrame({ url: feedItem.media_url, generation_id: feedItem.generation_id });
        toast.success("Start frame set from feed");
        return;
      }
      if (omniRefs.length < 6) {
        setOmniRefs((prev) => [
          ...prev,
          { url: feedItem.media_url, uploaded_url: feedItem.media_url, kind: "image", feed_id: feedItem.id },
        ]);
        toast.success("Reference attached from feed");
        return;
      }
      toast.error("No empty attachment slot.");
    }
  };

  const regenerate = async () => {
    if (!current) return;
    // Preserve the exact prompt & style/aspect for a fresh variation
    if (current.type === "image" || (!current.video_url && !current.views)) {
      // Image regenerate
      setPrompt(current.prompt || prompt);
      // small delay to let state settle; then call generate
      setTimeout(() => generateImage(), 50);
    } else if (current.video_url && current.type === "video") {
      // Video re-animate off the same source (best-effort — same motion prompt)
      if (current.edit_of || current.image_url) {
        // Re-animate uses the current image_url source; delegate to animate()
        animate();
      } else {
        toast.error("Cannot regenerate this video — original source missing.");
      }
    } else {
      toast.error("Regenerate unavailable for this output.");
    }
  };

  const generateTextVideo = async () => {
    if (!prompt.trim()) { toast.error("Speak your vision first."); return; }
    setGenerating(true);
    try {
      const ref_urls = buildRefUrls();
      const presetHint = resolveMotionPreset(motionPreset);
      const finalMotion = [presetHint, motionPrompt].filter((s) => s && s.trim()).join(". ");
      // Route through feed.enqueue so the clip lands in the Studio feed —
      // the direct api.post call that lived here previously was the bug
      // that caused videos to appear in the Gallery but never in the feed.
      // The enqueue path owns polling + progress + feed insertion + auto-
      // scroll + the ready-popup, all for free.
      const result = await feed.enqueue({
        mode: "video",
        prompt: withStyleSuffix(prompt),
        params: {
          motion_prompt: finalMotion,
          duration,
          video_resolution: videoResolution,
          aspect_ratio: aspect,
          reference_urls: ref_urls.length ? ref_urls : undefined,
          start_image_url: startFrame?.url || undefined,
          end_image_url: endFrame?.url || undefined,
          model_version: videoModel,
          ...stylePayload(),
        },
      });
      if (result?.ok && result?.data) {
        setCurrent(result.data);
        toast.success(ref_urls.length ? `Reel created with ${ref_urls.length} omni refs.` : "Reel created — Seedance breathed it live.");
      }
    } finally {
      setGenerating(false);
    }
  };

  const handleVideoFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("video/")) { toast.error("Please pick a video file."); return; }
    if (file.size > 40 * 1024 * 1024) { toast.error("Max size is 40MB."); return; }
    setUploadingVideo(true);
    setVideoPreview({ url: URL.createObjectURL(file), file, uploaded_url: null });
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload/video", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setVideoPreview((p) => p ? { ...p, uploaded_url: data.video_url } : p);
      toast.success("Video uploaded — ready to restyle.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
      setVideoPreview(null);
    } finally {
      setUploadingVideo(false);
    }
  };

  const generateVideoToVideo = async () => {
    if (!videoPreview?.uploaded_url) { toast.error("Upload a video first."); return; }
    if (!prompt.trim()) { toast.error("Describe how to restyle it."); return; }
    setGenerating(true);
    try {
      const ref_urls = buildRefUrls();
      const presetHint = resolveMotionPreset(motionPreset);
      const restylePrompt = presetHint ? `${prompt}. Camera: ${presetHint}` : prompt;
      // Route through the feed so the restyled clip shows up alongside every
      // other generation. `mode: "v2v"` maps to /generate/video-to-video which
      // is now async — the enqueue path handles polling + progress + refund.
      const result = await feed.enqueue({
        mode: "v2v",
        prompt: restylePrompt,
        params: {
          video_url: videoPreview.uploaded_url,
          prompt_augmented: withStyleSuffix(restylePrompt),
          duration,
          resolution: videoResolution,
          aspect_ratio: aspect,
          reference_urls: ref_urls.length ? ref_urls : undefined,
          start_image_url: startFrame?.url || undefined,
          end_image_url: endFrame?.url || undefined,
          ...stylePayload(),
        },
      });
      if (result?.ok && result?.data) {
        setCurrent(result.data);
        setVideoPreview(null);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Video-to-video failed");
    } finally {
      setGenerating(false);
    }
  };

  const submitVoiceClone = async () => {
    const file = voiceSampleRef.current?.files?.[0];
    if (!file) { toast.error("Pick a voice sample (mp3/wav/m4a)."); return; }
    if (!voiceCloneName.trim()) { toast.error("Name your voice."); return; }
    setVoiceCloning(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("name", voiceCloneName.trim());
      await api.post("/voice/clone", fd, { headers: { "Content-Type": "multipart/form-data" } });
      // Refresh voice roster + select mine
      const { data } = await api.get("/tts/voices");
      setVoices(data?.voices || []);
      setVoice("mine");
      setVoiceCloneOpen(false);
      toast.success("Voice cloned — narration will use your signature voice.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Voice clone failed");
    } finally {
      setVoiceCloning(false);
    }
  };

  const generateTurnaround = async () => {
    const hasPrompt = prompt.trim().length > 0;
    const hasRef = !!turnRefPreview;
    // Fall back to the current stage image (from a completed generation or a prior
    // upload). This lets "Generate Character Sheet" work directly off the active
    // reference image on the Character Sheet page without re-uploading.
    const hasStageSource = !!current?.generation_id && (current?.image_url || current?.type === "image");
    if (!hasPrompt && !hasRef && !hasStageSource) { toast.error("Describe a character or upload a reference image."); return; }
    setGenerating(true);
    try {
      let sourceGenId = null;
      if (hasRef) {
        setCurrent(null);
        const fd = new FormData();
        fd.append("file", turnRefPreview.file);
        fd.append("style", style);
        fd.append("note", `Turnaround reference — ${turnRefPreview.file.name || "image"}`);
        const { data: up } = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        sourceGenId = up.generation_id;
      } else if (hasStageSource) {
        // Reuse the active stage image directly — no re-upload.
        sourceGenId = current.generation_id;
      } else {
        setCurrent(null);
      }
      const ref_urls = buildRefUrls();
      // Route through the feed queue so the sheet lands as a feed card.
      // Previously called /generate/turnaround directly and only updated
      // `current`, meaning the sheet showed in the stage but never in the
      // feed — same bug as text-video. Enqueue handles polling + insertion.
      const result = await feed.enqueue({
        mode: "turnaround",
        prompt: withStyleSuffix(prompt),
        params: {
          aspect_ratio: aspect,
          resolution,
          reference_urls: ref_urls.length ? ref_urls : undefined,
          source_generation_id: sourceGenId,
          lock_character: lockCharacter,
          ...stylePayload(),
        },
      });
      if (result?.ok && result?.data) {
        setCurrent(result.data);
        toast.success(ref_urls.length ? `Character Sheet created with ${ref_urls.length} omni refs.` : "Character Sheet created — 10 views ready.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Character Sheet failed");
    } finally {
      setGenerating(false);
    }
  };

  const composeSheetBlob = () => new Promise(async (resolve) => {
    if (!current?.views) { resolve(null); return; }
    // Magazine-style layout: 12-column canvas, 3 rows with different tile widths.
    const cols = 12;
    const tile = 512;                 // base column unit
    const gap = 20;
    const labelH = 40;
    const headerH = 96;
    const rowGap = 32;
    const canvasW = cols * (tile / 3) + gap * (cols + 1) + 120; // canvas ~ 2560 wide
    // Compute row heights based on tile aspect (square) with tile widths per row
    const rowSpecs = SHEET_ROWS.map((r) => {
      const span = Math.floor(cols / r.keys.length);
      const tileW = span * (tile / 3) + (span - 1) * gap;
      return { ...r, span, tileW, tileH: tileW };
    });
    const canvasH = headerH + rowSpecs.reduce((sum, r) => sum + r.tileH + labelH, 0) + rowGap * (rowSpecs.length - 1) + 60;
    const canvas = document.createElement("canvas");
    canvas.width = canvasW; canvas.height = canvasH;
    const ctx = canvas.getContext("2d");
    // Backdrop
    ctx.fillStyle = "#0A0A0A"; ctx.fillRect(0, 0, canvasW, canvasH);
    // Header
    ctx.fillStyle = "#F2C94C"; ctx.font = "600 28px 'Unbounded', sans-serif";
    ctx.fillText("GENEO AI · CHARACTER SHEET", 40, 46);
    ctx.fillStyle = "#FFFFFF"; ctx.font = "13px 'Space Mono', monospace";
    ctx.fillText((current.prompt || "").slice(0, 140), 40, 70);
    ctx.fillStyle = "#00E5FF"; ctx.font = "11px 'Space Mono', monospace";
    const styleLine = `${styleLabel(current.style)}${current.style_b ? ` × ${styleLabel(current.style_b)} (${current.blend}%/${100 - current.blend}%)` : ""}`;
    ctx.fillText(styleLine.toUpperCase(), 40, 88);

    let cursorY = headerH;
    for (const row of rowSpecs) {
      // Row title
      ctx.fillStyle = "#F2C94C"; ctx.font = "600 11px 'Space Mono', monospace";
      ctx.fillText(row.title, 40, cursorY - 10);

      // Center row group
      const rowTotalW = row.keys.length * row.tileW + (row.keys.length - 1) * gap;
      const rowStartX = Math.max(40, (canvasW - rowTotalW) / 2);

      await Promise.all(row.keys.map((v, i) => new Promise((res) => {
        const x = rowStartX + i * (row.tileW + gap);
        const y = cursorY;
        // Label strip
        ctx.fillStyle = "#120E15"; ctx.fillRect(x, y, row.tileW, labelH);
        ctx.strokeStyle = "rgba(242,201,76,0.3)"; ctx.strokeRect(x + 0.5, y + 0.5, row.tileW - 1, labelH - 1);
        ctx.fillStyle = "#F2C94C"; ctx.font = "600 12px 'Space Mono', monospace";
        ctx.fillText(TURN_LABELS[v] || v.toUpperCase(), x + 12, y + 25);

        // Image tile
        ctx.fillStyle = "#120E15"; ctx.fillRect(x, y + labelH, row.tileW, row.tileH);
        const url = current.views?.[v]?.image_url;
        if (!url) { res(); return; }
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
          const ratio = Math.min(row.tileW / img.width, row.tileH / img.height);
          const dw = img.width * ratio, dh = img.height * ratio;
          ctx.drawImage(img, x + (row.tileW - dw) / 2, y + labelH + (row.tileH - dh) / 2, dw, dh);
          res();
        };
        img.onerror = () => res();
        img.src = url + (url.includes("?") ? "&" : "?") + "sheet=1";
      })));

      cursorY += labelH + row.tileH + rowGap;
    }
    canvas.toBlob((b) => resolve(b), "image/png");
  });

  const exportReferenceSheet = async () => {
    if (!current?.views) return;
    setExportingSheet(true);
    try {
      const blob = await composeSheetBlob();
      if (!blob) throw new Error("compose failed");
      const link = document.createElement("a");
      link.download = `geneo-character-sheet-${current.generation_id}.png`;
      link.href = URL.createObjectURL(blob);
      link.click();
      setTimeout(() => URL.revokeObjectURL(link.href), 5000);
      toast.success("Reference sheet downloaded.");
    } catch (e) {
      toast.error("Sheet export failed — try again.");
    } finally { setExportingSheet(false); }
  };

  const generateTurntable = async () => {
    if (!current?.generation_id) return;
    setSpinning(true);
    try {
      // Route through feed.enqueue so the turntable video lands in the
      // Studio feed as a video card (same insertion path as text-video).
      const result = await feed.enqueue({
        mode: "turntable",
        prompt: `${current.prompt || "subject"} · 360° turntable`,
        params: { source_generation_id: current.generation_id, duration: 5 },
      });
      if (result?.ok && result?.data) {
        setCurrent(result.data);
        toast.success("Turntable video ready.");
      }
    } finally { setSpinning(false); }
  };

  const generateStoryReel = async () => {
    if (!current?.generation_id) return;
    setReelBuilding(true);
    setStoryReel(null);
    try {
      const { data } = await api.post("/generate/story-reel", {
        generation_id: current.generation_id,
        include_video: true,
      });
      setStoryReel(data);
      toast.success("Story Reel ready — 15-second shareable clip.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Story Reel failed");
    } finally { setReelBuilding(false); }
  };

  const shareStoryReel = async () => {
    if (!storyReel?.video_url) return;
    const shareUrl = `${window.location.origin}/gallery?reel=${storyReel.generation_id}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "Geneo AI Story Reel", text: "Made in Geneo AI — Afro Sci-Fi Story Reel", url: shareUrl });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        toast.success("Share link copied.");
      }
    } catch (err) {
      // Share sheet dismissed by the user, or navigator.share threw AbortError
      if (err?.name !== "AbortError") console.debug("share dismissed", err);
    }
  };

  const downloadStoryReel = () => {
    if (!storyReel?.video_url) return;
    const link = document.createElement("a");
    link.href = storyReel.video_url;
    link.download = `geneo-story-${storyReel.generation_id}.mp4`;
    link.target = "_blank";
    link.click();
    toast.success("Downloading reel…");
  };

  const exportRigZip = async () => {
    if (!current?.views) return;
    setExportingZip(true);
    try {
      const zip = new JSZip();
      const rigDir = zip.folder(`geneo-rig-${current.generation_id}`);
      const sheetBlob = await composeSheetBlob();
      if (sheetBlob) rigDir.file("reference-sheet.png", sheetBlob);
      for (const v of TURN_VIEWS) {
        const url = current.views?.[v]?.image_url;
        if (!url) continue;
        try {
          const res = await fetch(url + (url.includes("?") ? "&" : "?") + "zip=1", { credentials: "include" });
          const b = await res.blob();
          const ext = (b.type.split("/")[1] || "png").split(";")[0];
          rigDir.file(`${v}.${ext}`, b);
        } catch (err) {
          // Skip a single view that fails to fetch — the zip still ships with the others + reference sheet
          console.debug(`rig-zip: skipped view ${v}`, err);
        }
      }
      rigDir.file("meta.json", JSON.stringify({
        generation_id: current.generation_id, prompt: current.prompt,
        style: current.style, style_b: current.style_b || null, blend: current.blend || null,
        created_at: current.created_at, views: TURN_VIEWS,
      }, null, 2));
      const blob = await zip.generateAsync({ type: "blob" });
      const link = document.createElement("a");
      link.download = `geneo-rig-${current.generation_id}.zip`;
      link.href = URL.createObjectURL(blob);
      link.click();
      setTimeout(() => URL.revokeObjectURL(link.href), 5000);
      toast.success("Rig pack downloaded.");
    } catch (e) {
      toast.error("Zip export failed — try again.");
    } finally { setExportingZip(false); }
  };

  const animate = async (sourceGen = null) => {
    let target = sourceGen || current;
    if (!target && mode === "pipeline") {
      if (!uploadPreview) { toast.error("Please choose or generate an image first."); return; }
      setAnimating(true);
      try {
        target = await pipelineUpload(uploadPreview.file, true);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Upload failed");
        setAnimating(false);
        return;
      }
    }
    if (!target) return;
    setAnimating(true);
    try {
      const presetHint = resolveMotionPreset(motionPreset);
      const finalMotion = [presetHint, motionPrompt].filter((s) => s && s.trim()).join(". ");
      // Route through `feed.enqueue` so the video render shows up as a feed
      // item that transparently polls `/api/jobs/:id` for async completion.
      // Previously we called `api.post("/generate/video")` directly and just
      // received a `{job_id, status:"queued"}` stub — the video never
      // appeared in the Feed even though it landed in the Gallery once the
      // job completed. Enqueuing hands off to `pollJob` which patches the
      // feed item to `done` with the final `video_url` once ready.
      const promptForFeed = target.prompt || finalMotion || "Animate";
      const result = await feed.enqueue({
        mode: "i2v",
        prompt: promptForFeed,
        params: {
          source_generation_id: target.generation_id,
          motion_prompt: finalMotion,
          duration,
          first_frame_url: startFrame?.url || undefined,
          last_frame_url: endFrame?.url || undefined,
          model_version: videoModel,
          ...stylePayload(),
        },
      });
      if (result?.ok && result?.data) {
        setCurrent(result.data);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Animation failed");
    } finally { setAnimating(false); }
  };

  const pipelineUpload = async (file, returnResult = false) => {
    if (!file) return null;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file."); return null; }
    if (file.size > 15 * 1024 * 1024) { toast.error("Max size is 15MB."); return null; }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("style", style);
    try {
      const { data } = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCurrent(data);
      setUploadPreview(null);
      setEditChain([{
        generation_id: data.generation_id,
        image_url: data.image_url,
        prompt: data.prompt,
        edit_instruction: null,
      }]);
      toast.success("Image uploaded — ready to route.");
      if (returnResult) return data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    }
    return null;
  };

  // Unified: switches to Character Sheet mode with current image pre-loaded as reference
  const sendToTurnaroundFlow = async () => {
    if (!current?.generation_id) return;
    // Try to fetch the current image as a File and pin it as turnaround reference
    const imgUrl = current.image_url;
    if (imgUrl) {
      try {
        const res = await fetch(imgUrl, { credentials: "include" });
        const blob = await res.blob();
        const filename = `${current.generation_id}.${(blob.type.split("/")[1] || "png").split(";")[0]}`;
        const file = new File([blob], filename, { type: blob.type });
        setTurnRefPreview({ url: URL.createObjectURL(file), file });
      } catch {
        // Fallback: just show URL as preview
        setTurnRefPreview({ url: imgUrl, file: null });
      }
    }
    // Preserve the prompt from the current image so the ref carries context
    if (current.prompt && !prompt.trim()) setPrompt(current.prompt.slice(0, 500));
    setMode("turnaround");
    toast.success("Reference locked — tap Generate Character Sheet.");
  };

  // Feed-menu: take any feed IMAGE item and preload it as the video generation
  // start frame, then switch the composer to AI Video mode. Same effect as
  // dragging the image into the composer and picking "Animate" — but from a
  // single cyan action pill on the feed card.
  const sendFeedItemToVideoStartFrame = (item) => {
    const imgUrl = item?.media_url || item?.image_url;
    if (!imgUrl) { toast.error("This item has no image to animate."); return; }
    if (item.kind === "video") { toast.error("Image to Video is image-only."); return; }
    setStartFrame({ url: imgUrl, generation_id: item.generation_id });
    if (item.prompt && !prompt.trim()) setPrompt(item.prompt.slice(0, 500));
    // Flip both the dock and the classic-editor modes so every downstream
    // control (aspect card, credit cost chip, submit route) reads "video".
    setDockMode("video");
    setMode("text-video");
    // Nudge scroll to the composer so the user sees the attached start frame.
    rearmSubjectLock();
    toast.success("Start frame set — type a motion prompt and Generate.");
  };

  // Feed-menu: take any feed item's image and pin it as the Character Sheet reference.
  const sendFeedItemToCharacterSheet = async (item) => {
    const imgUrl = item?.media_url || item?.image_url;
    if (!imgUrl) { toast.error("This item has no image to reference."); return; }
    try {
      const res = await fetch(imgUrl, { credentials: "include" });
      const blob = await res.blob();
      const filename = `${item.id || "feed"}.${(blob.type.split("/")[1] || "png").split(";")[0]}`;
      const file = new File([blob], filename, { type: blob.type });
      setTurnRefPreview({ url: URL.createObjectURL(file), file });
    } catch {
      setTurnRefPreview({ url: imgUrl, file: null });
    }
    if (item.prompt && !prompt.trim()) setPrompt(item.prompt.slice(0, 500));
    setMode("turnaround");
    toast.success("Reference locked — tap Generate Character Sheet.");
  };

  // Feed-inline: single-tap Style Sheet — fires a new queued generation that
  // uses THIS feed item's image as the subject and produces a composite
  // multi-angle character reference sheet in the same feed.
  const generateStyleSheetFromItem = async (item) => {
    if (!item?.generation_id) {
      toast.error("This item hasn't finished yet — try again in a moment.");
      return;
    }
    if (item.kind === "video") {
      toast.error("Style Sheet is image-only.");
      return;
    }
    toast.success("Rendering style sheet — the composite will appear in the feed.");
    await feed.enqueue({
      mode: "style-sheet",
      prompt: `${item.prompt || "reference"} · style sheet`,
      params: {
        source_generation_id: item.generation_id,
        aspect_ratio: "16:9",
      },
    });
  };

  const sendToPipelineFlow = () => {
    if (!current?.generation_id) return;
    setMode("pipeline");
    toast.success("Routed into the pipeline.");
  };

  const downloadCurrentImage = () => {
    const url = current?.video_url || current?.image_url;
    if (!url) return;
    const link = document.createElement("a");
    link.href = url;
    link.download = `geneo-${current.generation_id}.${current.video_url ? "mp4" : "png"}`;
    link.target = "_blank";
    link.click();
    toast.success("Downloading…");
  };

  const editImage = async () => {
    if (!current?.generation_id) return;
    if (!editPrompt.trim()) { toast.error("Describe the change first."); return; }
    setEditing(true);
    try {
      const ref_urls = buildRefUrls();
      const { data } = await api.post("/generate/edit", {
        generation_id: current.generation_id,
        edit_prompt: editPrompt,
        aspect_ratio: aspect,
        reference_urls: ref_urls.length ? ref_urls : undefined,
        ...stylePayload(),
      });
      // Push the new edit into the chain so breadcrumbs can show iteration lineage
      setEditChain((prev) => {
        const base = prev.length ? prev : [{
          generation_id: current.generation_id,
          image_url: current.image_url,
          prompt: current.prompt,
          edit_instruction: null,
        }];
        return [...base, {
          generation_id: data.generation_id,
          image_url: data.image_url,
          prompt: data.prompt,
          edit_instruction: editPrompt.trim(),
        }];
      });
      setCurrent(data);
      setEditPrompt("");
      toast.success("Edit applied — breadcrumb updated.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Edit failed");
    } finally { setEditing(false); }
  };

  const restoreFromBreadcrumb = async (crumb) => {
    // Fetch the generation record so we get all fields (bio, views, etc.)
    if (!crumb?.generation_id) return;
    try {
      const list = await api.get("/generations");
      const found = (list.data || []).find((g) => g.generation_id === crumb.generation_id);
      if (found) setCurrent(found);
      else setCurrent({ ...crumb, type: "image" });
      toast.success(`Rewound to step ${editChain.findIndex((c) => c.generation_id === crumb.generation_id) + 1}.`);
    } catch {
      setCurrent({ ...crumb, type: "image" });
    }
  };

  const rerollBio = async (tone) => {
    if (!current?.generation_id) return;
    const chosen = tone || bioTone;
    setBioTone(chosen);
    setBioRerolling(true);
    try {
      const { data } = await api.post("/generate/bio-reroll", { generation_id: current.generation_id, tone: chosen });
      // Update local doc — invalidate cached audio so user can regenerate voice-over
      setCurrent((c) => c ? { ...c, bio: data.bio, bio_tone: chosen, bio_audio_url: null, bio_audio_voice: null } : c);
      setAudioSrc(null);
      setIsPlaying(false);
      toast.success(`Bio recreated · ${chosen}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bio reroll failed");
    } finally { setBioRerolling(false); }
  };

  const playVoiceover = async () => {
    if (!current?.bio) { toast.error("No bio to narrate."); return; }
    if (audioRef.current && audioSrc && !isPlaying && audioRef.current.src) {
      // Resume existing
      audioRef.current.play().catch(() => {});
      return;
    }
    setTtsLoading(true);
    try {
      const { data } = await api.post("/generate/tts", { generation_id: current.generation_id, voice });
      setAudioSrc(data.audio_url);
      setCurrent((c) => c ? { ...c, bio_audio_url: data.audio_url, bio_audio_voice: data.voice } : c);
      // Play after src is set on next tick
      setTimeout(() => { audioRef.current?.play().catch(() => {}); }, 100);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Voice-over failed");
    } finally { setTtsLoading(false); }
  };

  const pauseVoiceover = () => {
    audioRef.current?.pause();
  };

  const pushToHistory = (gen) => {
    if (!gen?.generation_id) return;
    const item = {
      generation_id: gen.generation_id,
      image_url: gen.image_url,
      video_url: gen.video_url,
      turntable_video_url: gen.turntable_video_url,
      type: gen.type,
      prompt: (gen.prompt || "").slice(0, 80),
      views: gen.views ? Object.fromEntries(Object.entries(gen.views).map(([k, v]) => [k, { image_url: v.image_url }])) : undefined,
      routed_at: Date.now(),
    };
    setHistory((prev) => {
      const filtered = prev.filter((h) => h.generation_id !== item.generation_id);
      const next = [item, ...filtered].slice(0, 3);
      writeHistory(next);
      return next;
    });
  };

  const restoreFromHistory = (h) => {
    setCurrent(h);
    setEditChain([{ generation_id: h.generation_id, image_url: h.image_url, prompt: h.prompt, edit_instruction: null }]);
    toast.success("Routed back into pipeline.");
  };

  const toggleHistorySelect = (id) => {
    setHistorySelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const clearSelectedHistory = () => {
    if (historySelected.size === 0) { toast.error("Select at least one recent item."); return; }
    const next = history.filter((h) => !historySelected.has(h.generation_id));
    setHistory(next);
    writeHistory(next);
    toast.success(`Cleared ${historySelected.size} recent item${historySelected.size !== 1 ? "s" : ""}.`);
    setHistorySelected(new Set());
    setHistorySelectMode(false);
  };

  const clearAllHistory = () => {
    if (!window.confirm("Clear ALL recent pipeline items? This cannot be undone.")) return;
    setHistory([]);
    writeHistory([]);
    setHistorySelected(new Set());
    setHistorySelectMode(false);
    toast.success("Recent cleared.");
  };

  const sendHistoryToStoryboard = (h) => {
    if (!h?.image_url && !h?.views?.front?.image_url) {
      toast.error("This item has no image to send.");
      return;
    }
    navigate("/storyboard", {
      state: {
        addScene: {
          character_generation_id: h.generation_id,
          character_image_url: h.image_url || h.views?.front?.image_url,
          description: h.prompt || "",
        },
      },
    });
    toast.success("Sent to Storyboard — new scene added.");
  };

  // ── Consistent Character (Cast Pin) ─────────────────────────────
  // Consistent Character: Pinned character's front view is transparently
  // prepended to every generation's ref_urls (unless it's already attached).
  // When Ensemble / Trio mode is active, each partner's front view is also
  // prepended so every co-star lands in the same shot.
  const buildRefUrls = () => {
    const attached = omniRefs.map((r) => r.uploaded_url).filter(Boolean);
    const heads = [];
    if (pinnedCharacter?.front_url) heads.push(pinnedCharacter.front_url);
    ensembleCharacters.forEach((c) => { if (c?.front_url) heads.push(c.front_url); });
    const merged = [...heads, ...attached];
    // Dedup while preserving order
    return merged.filter((url, i) => url && merged.indexOf(url) === i);
  };
  // Toggle a cast member into Ensemble/Trio mode. The lead cannot co-star with
  // itself, and the partner list is capped at MAX_ENSEMBLE_PARTNERS. Tapping
  // the same id twice removes that partner.
  const toggleEnsemble = (genId) => {
    if (!genId) return;
    if (genId === pinnedCharacter?.character_generation_id) {
      toast.error("They're already the lead — pick a different co-star.");
      return;
    }
    setEnsembleIds((prev) => {
      if (prev.includes(genId)) {
        const next = prev.filter((x) => x !== genId);
        const partner = castLibrary.find((c) => c.character_generation_id === genId);
        toast.success(`${partner?.name || "Co-star"} removed from ${next.length === 1 ? "ensemble" : (next.length === 2 ? "trio" : "the scene")}.`);
        return next;
      }
      if (prev.length >= MAX_ENSEMBLE_PARTNERS) {
        toast.error("Trio is full (3 members max) — clear a co-star to swap.");
        return prev;
      }
      const next = [...prev, genId];
      const partner = castLibrary.find((c) => c.character_generation_id === genId);
      const modeLabel = next.length === 1 ? "Ensemble" : "Trio";
      const namesList = [pinnedCharacter?.name || "Lead", ...next.map((id) => castLibrary.find((c) => c.character_generation_id === id)?.name || "Co-star")];
      toast.success(`${modeLabel} active — ${namesList.join(" + ")} will co-star.`);
      return next;
    });
  };
  const clearEnsemble = () => {
    setEnsembleIds([]);
    toast.success("Ensemble cleared — back to solo cast.");
  };
  // Auto-drop any ensemble partner that leaves the library or becomes the lead.
  useEffect(() => {
    setEnsembleIds((prev) => {
      const filtered = prev.filter((id) =>
        castLibrary.some(
          (c) => c.character_generation_id === id
            && c.character_generation_id !== pinnedCharacter?.character_generation_id
        )
      );
      return filtered.length === prev.length ? prev : filtered;
    });
  }, [castLibrary, pinnedCharacter?.character_generation_id]);
  // On mount, load the user's Cast Library so the chip + popover reflect
  // reality across page loads. Refreshed after every pin/activate/remove.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/character/pinned");
        setCastLibrary(Array.isArray(data?.library) ? data.library : []);
      } catch {
        // Not authed or endpoint unavailable — treat as no cast.
      }
    })();
  }, []);

  const pinCurrentAsCast = async (opts = {}) => {
    if (!current?.generation_id || current.type !== "turnaround") {
      toast.error("Open a Character Sheet first, then pin it to your cast.");
      return;
    }
    setPinningCharacter(true);
    try {
      const { data } = await api.post("/character/pin", {
        character_generation_id: current.generation_id,
        name: opts.name || null,
      });
      setCastLibrary(data.library || []);
      toast.success(`${data.active?.name || "Character"} is now active — auto-attached to every new prompt.`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "object" ? detail.message : detail;
      toast.error(msg || "Couldn't pin the character.");
    } finally {
      setPinningCharacter(false);
    }
  };

  const activateCastMember = async (genId) => {
    if (!genId) return;
    try {
      const { data } = await api.post(`/character/pin/${encodeURIComponent(genId)}/activate`);
      setCastLibrary(data.library || []);
      toast.success(`${data.active?.name || "Character"} is now the active cast.`);
    } catch {
      toast.error("Couldn't switch cast.");
    }
  };

  const removeCastMember = async (genId) => {
    if (!genId) return;
    try {
      const { data } = await api.delete(`/character/pin/${encodeURIComponent(genId)}`);
      setCastLibrary(data.library || []);
      toast.success("Removed from cast library.");
    } catch {
      toast.error("Couldn't remove.");
    }
  };

  const clearAllCast = async () => {
    try {
      await api.delete("/character/pin");
      setCastLibrary([]);
      setCastMenuOpen(false);
      toast.success("Cast cleared — future prompts run without any pinned character.");
    } catch {
      toast.error("Failed to clear cast.");
    }
  };

  // Legacy alias kept for existing call-sites (cast-chip X button):
  const unpinCast = () => pinnedCharacter?.character_generation_id && removeCastMember(pinnedCharacter.character_generation_id);

  // Whether the currently-viewed Character Sheet is already in the library.
  const currentIsInCast = !!(
    current?.generation_id &&
    current?.type === "turnaround" &&
    castLibrary.some((c) => c.character_generation_id === current.generation_id)
  );
  const currentIsActive = pinnedCharacter?.character_generation_id === current?.generation_id;

  // ── Character Sheet · 4 primary actions ─────────────────────────
  // Send front view (the canonical reference) to Storyboard as a new scene.
  const sendSheetToStoryboard = () => {
    if (!current?.views?.front?.image_url) {
      toast.error("Character Sheet is still generating — try again in a moment.");
      return;
    }
    // Pull the character name from the pinned Cast Library entry (if this sheet
    // is pinned) or fall back to the first meaningful phrase in the prompt.
    const castMatch = castLibrary.find((c) => c.character_generation_id === current.generation_id);
    const characterName = castMatch?.name
      || (current.prompt || "").split(/[.·\n]/)[0].trim().slice(0, 40)
      || "";
    // Friendly labels for each of the 10 canonical Character Sheet views so
    // Storyboard scenes can tag every draggable thumb by name.
    const VIEW_LABELS = {
      front:      "Front",
      side:       "Side",
      back:       "Back",
      head_front: "Head",
      head_side:  "Profile",
      head_3q:    "¾ Head",
      head_back:  "Back Head",
      face:       "Face",
      hands:      "Hands",
      outfit:     "Outfit",
    };
    // Collect every rendered view URL + parallel tag so every angle carries
    // over labeled. Preserve the canonical order defined by VIEW_LABELS.
    const viewUrls = [];
    const viewTags = [];
    for (const key of Object.keys(VIEW_LABELS)) {
      const v = current.views?.[key];
      if (v?.image_url) {
        viewUrls.push(v.image_url);
        viewTags.push(VIEW_LABELS[key]);
      }
    }
    navigate("/storyboard", {
      state: {
        addScene: {
          character_generation_id: current.generation_id,
          character_image_url: current.views.front.image_url,
          character_name: characterName,
          description: current.prompt || "",
          omni_references: viewUrls,
          omni_reference_tags: viewTags,
        },
      },
    });
    toast.success(
      characterName
        ? `${characterName} sent to Storyboard with ${viewUrls.length} tagged reference views.`
        : `Character Sheet sent to Storyboard with ${viewUrls.length} tagged reference views.`
    );
  };

  // Take every view in the sheet, upload each as a video reference, and switch
  // to Text→Video mode with them all pinned as omni refs.
  const generateVideoFromSheet = async () => {
    const views = current?.views;
    if (!views) { toast.error("No character sheet found."); return; }
    const urls = Object.values(views).map((v) => v?.image_url).filter(Boolean);
    if (!urls.length) { toast.error("No views ready yet."); return; }
    toast.info(`Attaching ${urls.length} views as video references…`);
    try {
      const uploaded = [];
      for (const url of urls) {
        try {
          const res = await fetch(url, { credentials: "include" });
          const blob = await res.blob();
          const ext = (blob.type.split("/")[1] || "png").split(";")[0];
          const file = new File([blob], `sheet-view.${ext}`, { type: blob.type });
          const fd = new FormData();
          fd.append("file", file);
          fd.append("note", "Character Sheet view — video reference");
          const { data } = await api.post("/upload/image", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          uploaded.push({
            url: URL.createObjectURL(file),
            file,
            uploaded_url: data.image_url,
          });
        } catch (e) {
          console.warn("Sheet view upload failed", e);
        }
      }
      if (!uploaded.length) { toast.error("Couldn't attach any views."); return; }
      // Cap at 6 to respect the backend omni-ref limit
      setOmniRefs(uploaded.slice(0, 6));
      setMode("create");
      switchMode?.("video");
      setDockMode("video");
      if (current.prompt && !prompt.trim()) setPrompt(current.prompt.slice(0, 500));
      toast.success(`${uploaded.length} sheet views attached — Text → Video ready.`);
    } catch (e) {
      toast.error("Failed to prep video references.");
    }
  };

  // Send the front view back to the Image prompt as a pinned reference so users
  // can iterate on the same character with new prompts.
  const sendSheetFrontToImagePrompt = async () => {
    const url = current?.views?.front?.image_url;
    if (!url) { toast.error("Front view isn't ready yet."); return; }
    try {
      const res = await fetch(url, { credentials: "include" });
      const blob = await res.blob();
      const ext = (blob.type.split("/")[1] || "png").split(";")[0];
      const file = new File([blob], `sheet-front.${ext}`, { type: blob.type });
      const fd = new FormData();
      fd.append("file", file);
      fd.append("note", "Character Sheet · front view reference");
      const { data } = await api.post("/upload/image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setOmniRefs((prev) => {
        const cleaned = prev.filter((r) => r.uploaded_url !== data.image_url);
        return [{ url: URL.createObjectURL(file), file, uploaded_url: data.image_url }, ...cleaned].slice(0, 5);
      });
      setMode("create");
      switchMode?.("image");
      setDockMode("image");
      if (current.prompt && !prompt.trim()) setPrompt(current.prompt.slice(0, 500));
      toast.success("Front view locked as image reference — iterate away.");
    } catch (e) {
      toast.error("Couldn't attach front view.");
    }
  };

  const togglePublic = async () => {
    if (!current) return;
    try {
      const { data } = await api.patch(`/generations/${current.generation_id}/visibility`, { is_public: !current.is_public });
      setCurrent({ ...current, is_public: data.is_public });
      toast.success(data.is_public ? "Shared with the dynasty" : "Sealed in your vault");
    } catch { toast.error("Update failed"); }
  };

  // Mode switch — preserves `current` and prompt so "Send to X" flows survive.
  const switchMode = (id) => {
    setMode(id);
    // Video modes only support 16:9 / 9:16 / 1:1 — coerce if user was on an image-only aspect.
    if ((id === "text-video" || id === "video-to-video") && !["16:9", "9:16", "1:1"].includes(aspect)) {
      setAspect("16:9");
    }
  };

  const reset = () => {
    setCurrent(null);
    setUploadPreview(null);
    setTurnRefPreview(null);
    setPrompt("");
    setMotionPrompt("");
    setEditChain([]);
    setAudioSrc(null);
    setIsPlaying(false);
  };

  const hasStageImage = !!(current?.image_url || current?.views?.front?.image_url);
  const isTurnaround = current?.type === "turnaround";

  return (
    <div className="min-h-screen bg-[#07070C] glyph-bg relative">
      <AmbientBg variant="studio" />
      <Navbar />

      <div
        className="max-w-7xl mx-auto px-6 md:px-10 py-10"
        // Dynamic bottom padding = live dock footprint + a 24px guard so
        // the last feed card always keeps a comfortable gap from the top
        // of the composer (PRD requires ≥16px; 24px gives sub-pixel
        // headroom and a visual breathing space). `--geneo-dock-h` is
        // published by ChatPromptDock's ResizeObserver so the feed never
        // scrolls under the composer no matter how tall it grows (style
        // panel open, refs rail full, mode expanded, etc). Fallback 260px
        // matches the old static reserve so first-paint doesn't overlap.
        style={{ paddingBottom: "calc(var(--geneo-dock-h, 260px) + 24px)" }}
      >
        {/* Compact hero — Dreamina-style prompt-dock UX */}
        <div className="relative mb-8 rounded-3xl overflow-hidden border border-[#F2C94C]/30 bg-gradient-to-br from-[#1a0d24]/80 via-[#0A0A0A]/70 to-[#0d1f24]/80 p-6 md:p-7 backdrop-blur-xl">
          <div className="absolute -top-10 -right-10 w-56 h-56 rounded-full bg-[#F2C94C]/20 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-16 -left-16 w-72 h-72 rounded-full bg-[#EB5757]/12 blur-3xl pointer-events-none" />
          <div className="relative flex items-start justify-between flex-wrap gap-4">
            <div>
              <div className="inline-flex items-center gap-2 font-mono-hy text-[10px] text-[#F2C94C] mb-3 px-3 py-1 rounded-full bg-[#F2C94C]/10 border border-[#F2C94C]/30">
                <Zap className="w-3 h-3" /> STUDIO · v3.2 · CHAT
              </div>
              <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tight">
                Bring <span className="bg-gradient-to-r from-[#F2C94C] via-[#E27D60] to-[#00E5FF] bg-clip-text text-transparent">Afro Sci-Fi / Animation</span> to life
              </h1>
              <p className="font-body text-[#D9D2BE] mt-2 max-w-xl text-sm md:text-base">
                Type your vision below. Queue multiple generations at once — they run in parallel and stream into the feed.
              </p>
            </div>
            <div className="flex flex-col items-end gap-2">
              <div className="font-mono-hy text-[10px] text-white">NANO·BANANA × SEEDANCE × ELEVENLABS</div>
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-[#27AE60] pulse-ring" />
                <span className="font-mono-hy text-[9px] text-[#27AE60]">LIVE</span>
              </div>
            </div>
          </div>
        </div>

        {/* ── FEED ─ generated images/videos stream here ────────────────────── */}
        <div className="mb-6">
          <GenerationFeed
            items={feed.items}
            onEdit={handleFeedEdit}
            onRegenerate={handleFeedRegenerate}
            onRegenerateN={(item, n) => { feed.regenerateN(item, n); toast.success(`Firing ${n} variations…`); }}
            onRetry={handleFeedRetry}
            onDelete={(it) => feed.remove(it.id)}
            onOpenDetail={handleFeedOpenDetail}
            onUseAsReference={(item) => attachFeedItem(item)}
            onImageToVideo={(item) => sendFeedItemToVideoStartFrame(item)}
            onSendToCharacterSheet={(item) => sendFeedItemToCharacterSheet(item)}
            onGenerateStyleSheet={(item) => generateStyleSheetFromItem(item)}
            consistencyScores={consistencyScores}
            castActive={!!pinnedCharacter?.character_generation_id}
            emptyLabel="Type in the composer below, tap send, and watch your visions materialize."
          />
        </div>

        {/* Advanced Editor toggle bar removed — classic mode-tabs live behind
            `showAdvanced` state but no longer surface a UI dropdown. The
            legacy classic block remains gated on `showAdvanced` (which stays
            false), leaving the modern chat/feed flow as the only surface. */}

        {showAdvanced && (
        <div data-testid="advanced-editor-region">
        {/* Mode Switch — preserves image on switch */}
        <div className="inline-flex gap-1 p-1 mb-6 rounded-full bg-[#120E15] border border-[#F2C94C]/15">
          {MODES.map((m) => {
            const Icon = m.icon;
            const active = mode === m.id;
            return (
              <button
                key={m.id}
                onClick={() => switchMode(m.id)}
                data-testid={`mode-${m.id}-btn`}
                className={`font-mono-hy text-[10px] px-5 py-2 rounded-full transition-all flex items-center gap-2 ${
                  active ? "bg-[#F2C94C] text-[#0A0A0A]" : "text-white hover:text-[#F2C94C]"
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {m.label}
              </button>
            );
          })}
          {hasStageImage && (
            <button
              onClick={reset}
              data-testid="global-reset-btn"
              className="font-mono-hy text-[10px] px-3 py-2 rounded-full text-white hover:text-[#E27D60] transition-colors"
              title="Start over"
            >
              ↻ Reset
            </button>
          )}
        </div>

        <div className="grid grid-cols-12 gap-6">
          {/* Controls */}
          <aside className="col-span-12 lg:col-span-4 space-y-6">
            {mode === "create" || mode === "turnaround" || mode === "text-video" || mode === "video-to-video" ? (
              <>
                {/* Generation Panel removed — the dock composer at the bottom is now the single input.
                    All refs/frames/prompt/send live in the dock's Attach + Skills popovers. */}

                {/* Aspect Ratio card — shows in every generation mode */}
                <AspectRatioCard
                  mode={mode}
                  aspect={aspect}
                  setAspect={setAspect}
                  resolution={resolution}
                  setResolution={setResolution}
                  videoResolution={videoResolution}
                  setVideoResolution={setVideoResolution}
                />

                {mode === "text-video" && (
                  <div className="p-5 rounded-2xl bg-gradient-to-br from-[#0d1f24] via-[#120E15] to-[#1a1208] border border-[#00E5FF]/30">
                    <div className="font-mono-hy text-[10px] text-[#00E5FF] mb-2 flex items-center gap-1.5">
                      <Zap className="w-3 h-3" /> ONE-SHOT VIDEO
                    </div>
                    <p className="font-body text-sm text-white">
                      Skip the image step — Seedance goes straight from text to a cinematic 5-10s clip in your chosen style. Costs 3 credits.
                    </p>
                  </div>
                )}
              </>
            ) : (
              <>
                {/* Pipeline sidebar (stepper / aspect card / edit-the-image
                    panel with AI Assist) intentionally removed per product
                    request. The dock composer at the bottom handles all
                    controls; the main preview column is now the sole focus
                    when an image is loaded in pipeline mode. */}
              </>
            )}

            {/* STYLE PRESETS — multi-select spell cards for both image & video */}
            <div className="p-6 rounded-2xl bg-[#120E15] border border-[#F2C94C]/15">
              <div className="flex items-center justify-between mb-3">
                <label className="font-mono-hy text-[10px] text-white">Style Presets · image + video</label>
                {stylesArr.length > 0 && (
                  <button
                    onClick={clearStyles}
                    data-testid="classic-styles-clear"
                    className="font-mono-hy text-[9px] text-white hover:text-[#F2C94C] tracking-widest"
                  >
                    CLEAR ALL · {stylesArr.length}
                  </button>
                )}
              </div>

              {stylesArr.length >= 2 && (
                <div className="mb-4 p-3 rounded-xl bg-[#0A0A0A] border border-[#F2C94C]/25 flex items-start gap-3" data-testid="classic-styles-blend-info">
                  <StyleBlendPreview ids={stylesArr} size={72} testId="classic-blend-preview" />
                  <div className="flex-1 min-w-0">
                    <div className="font-mono-hy text-[9px] text-[#F2C94C] tracking-[0.2em] mb-1.5">EQUAL BLEND · {stylesArr.length} STYLES</div>
                    <div className="font-body text-[11px] text-white">
                      {stylesArr.map((id) => styleLabel(id)).join(" · ")}
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-4">
                {STYLE_GROUPS.map((group) => (
                  <div key={group}>
                    <div className="font-mono-hy text-[9px] text-white mb-2 flex items-center gap-2">
                      <span>{group}</span>
                      <div className="flex-1 h-px bg-[#F2C94C]/10" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {STYLES.filter((s) => s.group === group).map((s) => (
                        <StylePresetChip
                          key={s.id}
                          preset={s}
                          active={stylesArr.includes(s.id)}
                          onClick={() => toggleStyle(s.id)}
                          testId={`style-preset-${s.id}`}
                        />
                      ))}
                    </div>
                  </div>
                ))}
                {/* Dev Presets · admin+devMode only. Distinct gold-bordered
                    section so operators know these are experimental. */}
                {devMode && devStylePresets.length > 0 && (
                  <div data-testid="dev-style-presets-section" className="mt-2 p-3 rounded-xl border-2 border-[#F2C94C] bg-[#F2C94C]/[0.05]">
                    <div className="font-mono-hy text-[9px] text-[#F2C94C] tracking-[0.24em] mb-2 flex items-center gap-2">
                      <span>DEV PRESETS · EXPERIMENTAL</span>
                      <div className="flex-1 h-px bg-[#F2C94C]/25" />
                      <span className="font-mono-hy text-[8px] px-1.5 py-0.5 rounded bg-[#F2C94C] text-[#0A0A0A]">ADMIN</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {devStylePresets.map((s) => (
                        <StylePresetChip
                          key={s.id}
                          preset={s}
                          active={stylesArr.includes(s.id)}
                          onClick={() => toggleStyle(s.id)}
                          testId={`style-preset-${s.id}`}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <p className="font-mono-hy text-[9px] text-white mt-3">
                {stylesArr.length === 0
                  ? "No styles selected — Geneo will follow your prompt verbatim."
                  : stylesArr.length === 1
                  ? "Applied to both image creating and motion aesthetic."
                  : "All selected styles blend equally into every generation."}
              </p>
            </div>

            {mode === "turnaround" && (
              <div className="p-6 rounded-2xl bg-[#120E15] border border-[#F2C94C]/15">
                <label className="flex items-center justify-between gap-3 cursor-pointer">
                  <div>
                    <div className="font-mono-hy text-[10px] text-[#F2C94C] flex items-center gap-2 mb-1">
                      <Hexagon className="w-3 h-3" /> Consistency Lock
                    </div>
                    <div className="font-body text-xs text-white">Pipe context between views so all 4 stay the same character.</div>
                  </div>
                  <Switch data-testid="lock-character-toggle" checked={lockCharacter} onCheckedChange={setLockCharacter} />
                </label>
                {lockCharacter && (
                  <div className="mt-3 font-mono-hy text-[9px] text-[#00E5FF] flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#00E5FF] pulse-ring" />
                    LOCKED — shared session, tighter coherence
                  </div>
                )}
              </div>
            )}

            {/* Primary Generate CTAs live inside the Unified Generation Panel now.
                Character Sheet · 4 primary actions requested by product. */}
            {(mode === "turnaround" || isTurnaround) && current?.views && (
              <Toolbar label="CHARACTER SHEET · ACTIONS" accent="gold" testId="character-sheet-actions-toolbar">
                <ToolChip
                  icon={Film}
                  label="Send to Storyboard"
                  sublabel="carry name + 10 reference views"
                  onClick={sendSheetToStoryboard}
                  accent="cyan"
                  shape="pill"
                  variant="solid"
                  size="lg"
                  fullWidth
                  testId="cs-send-storyboard-btn"
                />
                <ToolChip
                  icon={Download}
                  label={exportingSheet ? "Composing sheet…" : "Download"}
                  onClick={exportReferenceSheet}
                  disabled={exportingSheet}
                  loading={exportingSheet}
                  accent="gold"
                  shape="pill"
                  variant="solid"
                  size="lg"
                  fullWidth
                  testId="cs-download-btn"
                />
                <ToolChip
                  icon={Wand2}
                  label="Generate Video"
                  sublabel="all sheet frames as refs"
                  onClick={generateVideoFromSheet}
                  accent="cyan"
                  shape="pill"
                  variant="solid"
                  size="lg"
                  fullWidth
                  testId="cs-generate-video-btn"
                />
                <ToolChip
                  icon={ArrowLeft}
                  label="Back to Image Prompt"
                  sublabel="lock front view as reference"
                  onClick={sendSheetFrontToImagePrompt}
                  accent="cyan"
                  shape="pill"
                  variant="solid"
                  size="lg"
                  fullWidth
                  testId="cs-back-image-btn"
                />
                {/* Pin as Cast — one-tap chip that adds this character to the
                    Cast Library (max 5). Active member auto-attaches to every
                    subsequent generation. */}
                {currentIsActive ? (
                  <ToolChip
                    icon={Check}
                    label="Active in Cast Library"
                    sublabel="tap to remove"
                    onClick={() => removeCastMember(current.generation_id)}
                    accent="gold"
                    shape="pill"
                    variant="outline"
                    size="lg"
                    fullWidth
                    testId="cs-unpin-cast-btn"
                  />
                ) : currentIsInCast ? (
                  <ToolChip
                    icon={Users}
                    label="Set as Active Cast"
                    sublabel={`in library · ${castLibrary.length}/5`}
                    onClick={() => activateCastMember(current.generation_id)}
                    accent="gold"
                    shape="pill"
                    variant="solid"
                    size="lg"
                    fullWidth
                    testId="cs-activate-cast-btn"
                  />
                ) : (
                  <ToolChip
                    icon={Users}
                    label={pinningCharacter ? "Pinning…" : "Add to Cast Library"}
                    sublabel={`star in every future prompt · ${castLibrary.length}/5`}
                    onClick={() => pinCurrentAsCast()}
                    disabled={pinningCharacter || castLibrary.length >= 5}
                    loading={pinningCharacter}
                    accent="gold"
                    shape="pill"
                    variant="solid"
                    size="lg"
                    fullWidth
                    testId="cs-pin-cast-btn"
                  />
                )}
                {/* Secondary follow-ups (kept for power users) */}
                <div className="grid grid-cols-2 gap-2 w-full pt-2 border-t border-white/[0.06]">
                  <ToolChip
                    icon={RotateCw}
                    label={spinning ? "Spinning…" : "Turntable"}
                    onClick={generateTurntable}
                    disabled={spinning}
                    loading={spinning}
                    accent="gold"
                    shape="square"
                    testId="generate-turntable-btn"
                  />
                  <ToolChip
                    icon={Film}
                    label={reelBuilding ? "Reel…" : "Story Reel"}
                    onClick={generateStoryReel}
                    disabled={reelBuilding}
                    loading={reelBuilding}
                    accent="gold"
                    shape="square"
                    testId="generate-story-reel-btn"
                  />
                </div>
              </Toolbar>
            )}
            {mode === "pipeline" && current && (
              <ToolChip
                icon={Download}
                label={`Download ${current.video_url ? "Video" : "Image"}`}
                onClick={downloadCurrentImage}
                accent="gold"
                shape="pill"
                size="lg"
                variant="solid"
                fullWidth
                testId="pipeline-download-btn"
              />
            )}
          </aside>

          {/* Stage */}
          <main className="col-span-12 lg:col-span-8">
            <div className="relative rounded-3xl bg-[#120E15] border border-[#F2C94C]/15 min-h-[560px] overflow-hidden flex items-center justify-center">
              {/* Empty */}
              {!current && !generating && !animating && !uploadPreview && (
                <div className="text-center p-12 relative w-full">
                  {mode === "turnaround" && turnRefPreview && (
                    <img src={turnRefPreview.url} alt="ghost reference" data-testid="ghost-overlay"
                      className="absolute inset-0 m-auto max-h-full max-w-full object-contain opacity-15 mix-blend-luminosity pointer-events-none"
                      style={{ maskImage: "linear-gradient(180deg, rgba(0,0,0,1) 30%, rgba(0,0,0,0.2) 100%)", WebkitMaskImage: "linear-gradient(180deg, rgba(0,0,0,1) 30%, rgba(0,0,0,0.2) 100%)" }} />
                  )}
                  <div className="relative">
                    <div className="w-20 h-20 mx-auto rounded-2xl bg-[#1A1520] border border-[#F2C94C]/20 flex items-center justify-center mb-6 floaty">
                      {mode === "turnaround" ? <Users className="w-10 h-10 text-[#F2C94C]/60" /> : <Film className="w-10 h-10 text-[#00E5FF]/60" />}
                    </div>
                    <div className="font-mono-hy text-[10px] text-white mb-2">
                      // Stage · {chosenStyle?.name}
                      {mode === "turnaround" && turnRefPreview && <span className="ml-2 text-[#00E5FF]">· REFERENCE LOCKED</span>}
                    </div>
                    <p className="font-display text-2xl text-white">
                      {mode === "create" && "Speak your vision, then breathe it into motion."}
                      {mode === "text-video" && "Type your vision. Seedance breathes it straight into a video."}
                      {mode === "video-to-video" && (videoPreview ? "Clip locked — describe the restyle and hit Restyle Video." : "Upload a short clip, then Seedance will restyle every frame.")}
                      {mode === "pipeline" && "Upload or generate an image to start the pipeline."}
                      {mode === "turnaround" && (turnRefPreview ? "Reference anchored — tap Generate Character Sheet." : "Describe a character, or upload a reference — get 10 studio views on one sheet.")}
                    </p>
                  </div>
                </div>
              )}

              {generating && (
                <div className="text-center p-12">
                  <div className="relative w-24 h-24 mx-auto mb-6">
                    <div className="absolute inset-0 rounded-full border-2 border-[#F2C94C]/20" />
                    <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-[#F2C94C] animate-spin" />
                    <Sparkles className="absolute inset-0 m-auto w-8 h-8 text-[#F2C94C]" />
                  </div>
                  <div className="font-mono-hy text-[11px] text-[#F2C94C]">
                    {mode === "turnaround" ? "Composing 10-view Character Sheet…" : "Nano Banana creating…"}
                  </div>
                  {mode === "turnaround" && (
                    <div className="font-mono-hy text-[9px] text-white mt-2">Full body · Head study · Close-ups · ~120s</div>
                  )}
                </div>
              )}

              {current && !generating && (
                <div className="relative w-full h-full flex flex-col">
                  <div className="relative flex-1 flex items-center justify-center p-6 md:p-10">
                    {current.type === "video" && current.video_url ? (
                      <div className="relative max-w-full">
                        <video ref={stageVideoRef} data-testid="stage-video" src={current.video_url} controls autoPlay loop muted={!soundOn}
                          className="max-h-[520px] max-w-full rounded-2xl border border-[#F2C94C]/20" />
                        <SoundControl
                          soundOn={soundOn}
                          volume={volume}
                          onToggle={toggleSound}
                          onVolumeChange={handleVolume}
                          variant="overlay"
                          accent="cyan"
                          testId="stage-sound"
                        />
                      </div>
                    ) : current.type === "turnaround" && current.views ? (
                      current.turntable_video_url ? (
                        <div className="relative max-w-full">
                          <video ref={turntableVideoRef} data-testid="stage-turntable-video" src={current.turntable_video_url} controls autoPlay loop muted={!soundOn}
                            className="max-h-[520px] max-w-full rounded-2xl border border-[#00E5FF]/40" />
                          <SoundControl
                            soundOn={soundOn}
                            volume={volume}
                            onToggle={toggleSound}
                            onVolumeChange={handleVolume}
                            variant="overlay"
                            accent="cyan"
                            testId="turntable-sound"
                          />
                        </div>
                      ) : (
                        <div className="w-full space-y-4" data-testid="turnaround-grid">
                          {SHEET_ROWS.map((row) => (
                            <div key={row.title}>
                              <div className="font-mono-hy text-[9px] text-[#F2C94C] tracking-[0.24em] mb-2 pl-1">
                                {row.title}
                              </div>
                              <div
                                className="grid gap-3"
                                style={{ gridTemplateColumns: `repeat(${row.keys.length}, minmax(0, 1fr))` }}
                              >
                                {row.keys.map((v) => {
                                  const view = current.views[v];
                                  return (
                                    <div key={v} className="relative aspect-square rounded-xl overflow-hidden border border-[#F2C94C]/20 bg-[#0A0A0A]">
                                      {view?.image_url ? (
                                        <img data-testid={`turnaround-view-${v}`} src={view.image_url} alt={v} className="w-full h-full object-cover" />
                                      ) : (<div className="w-full h-full flex items-center justify-center"><Loader2 className="w-6 h-6 text-[#F2C94C] animate-spin" /></div>)}
                                      <div className="absolute top-2 left-2 font-mono-hy text-[9px] px-2 py-0.5 rounded-full bg-[#0A0A0A]/85 backdrop-blur border border-[#F2C94C]/30 text-[#F2C94C]">
                                        {TURN_LABELS[v]}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      )
                    ) : (
                      <img data-testid="stage-image" src={current.image_url || current.image_data} alt={current.prompt}
                        className="max-h-[520px] max-w-full rounded-2xl border border-[#F2C94C]/20" />
                    )}
                    {(animating || spinning) && (
                      <div className="absolute inset-0 flex items-center justify-center bg-[#0A0A0A]/80 backdrop-blur-sm rounded-3xl">
                        <div className="text-center">
                          <div className="relative w-24 h-24 mx-auto mb-6">
                            <div className="absolute inset-0 rounded-full border-2 border-[#00E5FF]/20" />
                            <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-[#00E5FF] animate-spin" />
                            {spinning ? <RotateCw className="absolute inset-0 m-auto w-8 h-8 text-[#00E5FF]" /> : <Film className="absolute inset-0 m-auto w-8 h-8 text-[#00E5FF]" />}
                          </div>
                          <div className="font-mono-hy text-[11px] text-[#00E5FF]">
                            {spinning ? "360° turntable spinning…" : "Seedance breathing…"}
                          </div>
                          <div className="font-mono-hy text-[9px] text-white mt-2">This may take 30-90 seconds</div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Prompt strip + Public toggle + Download */}
                  <div className="p-6 border-t border-[#F2C94C]/10 flex items-center justify-between flex-wrap gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="font-mono-hy text-[9px] text-white mb-1">
                        {current.video_url ? "VIDEO · MOTION" : current.type === "turnaround" ? "CHARACTER · SHEET" : "PROMPT"}
                      </div>
                      <div className="font-body text-sm text-white truncate max-w-md">
                        {current.video_url ? (current.motion_prompt || "cinematic motion") : current.prompt}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={togglePublic} data-testid="toggle-public-btn"
                        className={`font-mono-hy text-[10px] px-4 py-2 rounded-full border transition-all ${
                          current.is_public ? "bg-[#27AE60]/20 border-[#27AE60] text-[#27AE60]" : "border-[#F2C94C]/20 text-white hover:border-[#F2C94C]/50"
                        }`}>
                        {current.is_public ? "◉ Public" : "○ Private"}
                      </button>
                      <a href={current.video_url || current.image_url}
                        download={`geneo-${current.generation_id}.${current.video_url ? "mp4" : "png"}`}
                        target="_blank" rel="noreferrer" data-testid="download-btn"
                        className="font-mono-hy text-[10px] px-4 py-2 rounded-full border border-[#F2C94C]/20 hover:border-[#F2C94C] text-white transition-all flex items-center gap-2">
                        <Download className="w-3.5 h-3.5" /> Download
                      </a>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* STORY REEL PREVIEW — appears after user generates a shareable 15s reel */}
            {storyReel && (
              <div className="mt-4 rounded-3xl bg-gradient-to-br from-[#120E15] via-[#0A0A0A] to-[#120E15] border border-[#F2C94C]/25 overflow-hidden" data-testid="story-reel-panel">
                <div className="p-4 border-b border-[#F2C94C]/10 flex items-center gap-3 flex-wrap">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#F2C94C] to-[#FF6B4A] flex items-center justify-center">
                    <Film className="w-4 h-4 text-[#0A0A0A]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-mono-hy text-[10px] text-[#F2C94C]">STORY REEL · SHAREABLE · 15S</div>
                    <div className="font-body text-xs text-white truncate">
                      {storyReel.has_narration ? "Narrated bio" : "Silent"} · {storyReel.has_tail_video ? "with motion tail" : "still slideshow"}
                    </div>
                  </div>
                  <button onClick={shareStoryReel} data-testid="share-story-reel-btn"
                    className="font-mono-hy text-[10px] px-4 py-2 rounded-full bg-[#F2C94C] text-[#0A0A0A] hover:bg-[#F2D06B] transition-all flex items-center gap-2">
                    <Send className="w-3.5 h-3.5" /> Share
                  </button>
                  <button onClick={downloadStoryReel} data-testid="download-story-reel-btn"
                    className="font-mono-hy text-[10px] px-4 py-2 rounded-full border border-[#F2C94C]/25 hover:border-[#F2C94C] text-white transition-all flex items-center gap-2">
                    <Download className="w-3.5 h-3.5" /> MP4
                  </button>
                </div>
                <div className="p-4 flex justify-center bg-[#0A0A0A]">
                  <div className="relative">
                    <video
                      ref={storyReelVideoRef}
                      key={storyReel.generation_id}
                      src={storyReel.video_url}
                      controls
                      autoPlay
                      loop
                      playsInline
                      muted={!soundOn}
                      data-testid="story-reel-video"
                      className="max-h-[520px] rounded-xl border border-[#F2C94C]/20"
                      style={{ aspectRatio: "9 / 16", maxWidth: "300px", width: "100%", background: "#000" }}
                    />
                    <SoundControl
                      soundOn={soundOn}
                      volume={volume}
                      onToggle={toggleSound}
                      onVolumeChange={handleVolume}
                      variant="overlay"
                      accent="gold"
                      testId="reel-sound"
                    />
                  </div>
                </div>
              </div>
            )}
            {hasStageImage && (
              <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="stage-action-row">
                {/* On the Character Sheet page, the Image → Video action is replaced with a
                    prominent gold "Generate Character Sheet" primary action that mirrors the
                    Character Sheet tab styling. On every other mode, the classic cyan Image →
                    Video button stays exactly as before. */}
                {mode === "turnaround" && !isTurnaround ? (
                  <button
                    onClick={generateTurnaround}
                    disabled={generating}
                    data-testid="stage-generate-sheet-btn"
                    className="font-mono-hy text-xs px-6 py-4 rounded-2xl bg-[#F2C94C] text-[#0A0A0A] hover:bg-[#F2D06B] transition-all hover:-translate-y-0.5 shadow-[0_0_28px_-4px_rgba(242,201,76,0.75)] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
                  >
                    {generating ? <><Loader2 className="w-4 h-4 animate-spin" /> Composing 10-view Character Sheet…</> : <><Users className="w-4 h-4" /> Generate Character Sheet · 5 cr</>}
                  </button>
                ) : (
                  !isTurnaround && !current.video_url && (
                    <button
                      onClick={() => animate()}
                      disabled={animating}
                      data-testid="stage-animate-btn"
                      className="font-mono-hy text-xs px-6 py-4 rounded-2xl bg-[#00E5FF] text-[#0A0A0A] hover:bg-[#7BF0FF] transition-all hover:-translate-y-0.5 glow-cyan disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
                    >
                      {animating ? <><Loader2 className="w-4 h-4 animate-spin" /> Seedance breathing…</> : <><Film className="w-4 h-4" /> Image → Video · {videoModel === "2.5" ? 10 : 3} cr</>}
                    </button>
                  )
                )}
                {/* "Send to Character Sheet" stage button removed per product request. */}
                {/* Send to Image Pipeline — visible in Create mode */}
                {mode === "create" && !isTurnaround && (
                  <button
                    onClick={sendToPipelineFlow}
                    data-testid="stage-pipeline-btn"
                    className="font-mono-hy text-xs px-6 py-4 rounded-2xl border-2 border-[#E27D60]/60 text-[#E27D60] hover:bg-[#E27D60]/10 hover:border-[#E27D60] transition-all hover:-translate-y-0.5 flex items-center justify-center gap-3"
                  >
                    <Send className="w-4 h-4" /> Send to Image Pipeline <ArrowRight className="w-4 h-4" />
                  </button>
                )}
                {/* Regenerate — re-run same prompt/settings for a fresh variation */}
                {!isTurnaround && (
                  <button
                    onClick={regenerate}
                    disabled={generating || animating}
                    data-testid="stage-regenerate-btn"
                    className="font-mono-hy text-xs px-6 py-4 rounded-2xl border-2 border-[#F2C94C]/40 text-[#F2C94C] hover:bg-[#F2C94C]/10 hover:border-[#F2C94C] transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
                  >
                    <RefreshCw className="w-4 h-4" /> Regenerate
                  </button>
                )}
                {/* Regenerate (from pipeline mode with an uploaded image, offer Generate as an alt route) */}
                {mode === "pipeline" && !isTurnaround && (
                  <button
                    onClick={() => setMode("create")}
                    data-testid="stage-create-btn"
                    className="font-mono-hy text-xs px-6 py-4 rounded-2xl border-2 border-[#E27D60]/60 text-[#E27D60] hover:bg-[#E27D60]/10 hover:border-[#E27D60] transition-all hover:-translate-y-0.5 flex items-center justify-center gap-3"
                  >
                    <Wand2 className="w-4 h-4" /> Create New Image <ArrowRight className="w-4 h-4" />
                  </button>
                )}
                {/* Clear Stage — reset everything and go back to empty stage */}
                <button
                  onClick={reset}
                  data-testid="stage-clear-btn"
                  className="font-mono-hy text-xs px-6 py-4 rounded-2xl border-2 border-[#605C50]/40 text-white hover:bg-[#E27D60]/10 hover:border-[#E27D60] hover:text-[#E27D60] transition-all hover:-translate-y-0.5 flex items-center justify-center gap-3"
                >
                  <X className="w-4 h-4" /> Clear Stage
                </button>
              </div>
            )}

            {/* EDIT HISTORY BREADCRUMB STRIP */}
            {editChain.length > 1 && (
              <div className="mt-5 p-4 rounded-2xl bg-[#120E15] border border-[#F2C94C]/15" data-testid="edit-breadcrumb-strip">
                <div className="font-mono-hy text-[10px] text-[#F2C94C] mb-3 flex items-center gap-2">
                  <RefreshCw className="w-3 h-3" /> // Edit history · tap to compare iterations
                </div>
                <div className="flex items-center gap-2 overflow-x-auto pb-1">
                  {editChain.map((c, i) => {
                    const isActive = c.generation_id === current?.generation_id;
                    return (
                      <div key={c.generation_id} className="flex items-center gap-2 shrink-0">
                        <button
                          onClick={() => restoreFromBreadcrumb(c)}
                          data-testid={`breadcrumb-step-${i}`}
                          className={`group relative w-24 h-24 rounded-xl overflow-hidden border transition-all ${
                            isActive ? "border-[#F2C94C] ring-2 ring-[#F2C94C]/40" : "border-[#F2C94C]/15 hover:border-[#F2C94C]/50"
                          }`}
                        >
                          {c.image_url ? (
                            <img src={c.image_url} alt={`step ${i}`} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full bg-[#0A0A0A]" />
                          )}
                          <div className="absolute top-1 left-1 font-mono-hy text-[9px] px-1.5 py-0.5 rounded bg-[#0A0A0A]/85 text-[#F2C94C]">
                            {i === 0 ? "ORIG" : `E${i}`}
                          </div>
                          {isActive && (
                            <div className="absolute bottom-1 right-1 w-4 h-4 rounded-full bg-[#F2C94C] text-[#0A0A0A] flex items-center justify-center">
                              <Check className="w-2.5 h-2.5" />
                            </div>
                          )}
                        </button>
                        {i < editChain.length - 1 && <ChevronRight className="w-3.5 h-3.5 text-white shrink-0" />}
                      </div>
                    );
                  })}
                </div>
                {editChain.length > 1 && (
                  <div className="mt-2 font-mono-hy text-[9px] text-white truncate">
                    Last edit: <span className="text-white">"{editChain[editChain.length - 1].edit_instruction || "—"}"</span>
                  </div>
                )}

                {/* BEFORE / AFTER COMPARE — inline slider between the two most recent steps */}
                {editChain.length >= 2 && (() => {
                  const afterStep = editChain[editChain.length - 1];
                  const beforeStep = editChain[editChain.length - 2];
                  const isBgRemoval = (current?.edit_op === "bg_removal") ||
                    ((afterStep?.edit_instruction || "").toLowerCase().includes("background"));
                  return (
                    <div className="mt-4 pt-4 border-t border-[#F2C94C]/10" data-testid="edit-compare-block">
                      <div className="flex items-center justify-between mb-3">
                        <div className="font-mono-hy text-[10px] text-[#F2C94C] tracking-[0.2em] flex items-center gap-1.5">
                          <GitCompareArrows className="w-3 h-3" /> BEFORE · AFTER · DRAG TO REVEAL
                        </div>
                        <button
                          type="button"
                          onClick={() => setCompareOpen(true)}
                          data-testid="edit-compare-expand"
                          className="font-mono-hy text-[9px] text-[#00E5FF] hover:text-[#F2C94C] tracking-widest px-2 py-1 rounded-full border border-[#00E5FF]/30 hover:border-[#F2C94C]/60 transition-all"
                        >
                          EXPAND ↗
                        </button>
                      </div>
                      <div className="max-w-md mx-auto">
                        <BeforeAfterSlider
                          before={beforeStep.image_url}
                          after={afterStep.image_url}
                          showTransparencyChecker={isBgRemoval}
                          testId="edit-inline-compare"
                        />
                      </div>
                      <p className="font-mono-hy text-[9px] text-white mt-2 text-center">
                        Drag the gold handle to verify the subject stayed identical.
                      </p>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Compare full-screen modal */}
            {editChain.length >= 2 && (() => {
              const afterStep = editChain[editChain.length - 1];
              const beforeStep = editChain[editChain.length - 2];
              const isBgRemoval = (current?.edit_op === "bg_removal") ||
                ((afterStep?.edit_instruction || "").toLowerCase().includes("background"));
              return (
                <BeforeAfterSliderModal
                  open={compareOpen}
                  onClose={() => setCompareOpen(false)}
                  before={beforeStep.image_url}
                  after={afterStep.image_url}
                  showTransparencyChecker={isBgRemoval}
                />
              );
            })()}

            {/* filler to keep JSX shape intact (breadcrumb closer already placed above) */}

            {/* BIO CARD — regenerate + voice-over */}
            {isTurnaround && (
              <BioCard
                current={current}
                tones={BIO_TONES}
                bioTone={bioTone}
                bioRerolling={bioRerolling}
                onReroll={rerollBio}
                voices={voices}
                voice={voice}
                onVoiceChange={(v) => { setVoice(v); setAudioSrc(null); setIsPlaying(false); }}
                ttsLoading={ttsLoading}
                isPlaying={isPlaying}
                onPlay={playVoiceover}
                onPause={pauseVoiceover}
                audioSrc={audioSrc}
                audioRef={audioRef}
                onOpenVoiceClone={() => setVoiceCloneOpen(true)}
                onAudioPlay={() => setIsPlaying(true)}
                onAudioPause={() => setIsPlaying(false)}
                onAudioEnded={() => setIsPlaying(false)}
              />
            )}

            {/* PIPELINE HISTORY */}
            <PipelineHistoryStrip
              history={history}
              historySelectMode={historySelectMode}
              historySelected={historySelected}
              onStartSelect={() => setHistorySelectMode(true)}
              onCancelSelect={() => { setHistorySelectMode(false); setHistorySelected(new Set()); }}
              onClearAll={clearAllHistory}
              onClearSelected={clearSelectedHistory}
              onToggleSelect={toggleHistorySelect}
              onRestore={restoreFromHistory}
              onSendToStoryboard={sendHistoryToStoryboard}
            />
          </main>
        </div>
        </div>
        )}
      </div>

      {/* ── VIDEO READY POPUP — fires when a video job flips to done ────── */}
      <VideoReadyPopup
        item={videoReady}
        onDismiss={() => setVideoReady(null)}
        onSendToFeed={sendVideoResultToFeed}
        onOpenDetail={(it) => { handleFeedOpenDetail(it); setVideoReady(null); }}
      />

      {/* ── IMAGE DETAIL DIALOG — focused image view opened from the feed ─── */}
      <ImageDetailDialog
        item={detailItem}
        onClose={() => setDetailItem(null)}
        onRegenerate={handleFeedRegenerate}
        onCreateNew={handleCreateNewFromDetail}
      />

      {/* ── NEW RESULTS PILL — floats at the top when the user is scrolled
          away from the newest card and fresh renders land. Jumps to the
          newest result on tap. ─────────────────────────────────────────── */}
      <NewResultsPill count={newResults.count} onJump={newResults.jumpToNewest} />

      {/* ── SESSION SUBJECT LOCK PILL — character/object consistency ─────── */}
      <SubjectLockPill
        lock={subjectLock}
        onClear={clearSubjectLock}
        pastItems={feed.items}
        onReplace={(item) => {
          setSubjectLock({
            url: item.media_url,
            uploaded_url: item.media_url,
            uploaded_urls: [item.media_url],
            source: "generation",
            prompt: item.prompt,
            enabled: true,
            locked_at: new Date().toISOString(),
          });
          toast.success("Subject swapped — future generations will carry the new identity.");
        }}
        onApplyMulti={(items) => {
          if (!items?.length) return;
          const urls = items.map((it) => it.media_url).filter(Boolean);
          if (!urls.length) return;
          // Union with any currently-locked URLs so APPLY adds to the lock
          // rather than replacing it (users can still CLEAR for a hard reset).
          const existing = Array.isArray(subjectLock?.uploaded_urls) && subjectLock.uploaded_urls.length
            ? subjectLock.uploaded_urls
            : (subjectLock?.uploaded_url ? [subjectLock.uploaded_url] : []);
          const merged = Array.from(new Set([...existing, ...urls])).slice(0, 5);
          const first = items[0];
          setSubjectLock({
            url: subjectLock?.url || first.media_url,
            uploaded_url: merged[0],
            uploaded_urls: merged,
            source: subjectLock?.source || "generation",
            prompt: merged.length > 1
              ? `${first.prompt || "subject"} +${merged.length - 1} more`
              : first.prompt,
            enabled: true,
            locked_at: new Date().toISOString(),
          });
          toast.success(
            merged.length > 1
              ? `${merged.length} subjects locked — all will carry into future generations.`
              : "Subject swapped — future generations will carry the new identity."
          );
        }}
        onRemoveLockedUrl={(urlToRemove) => {
          if (!subjectLock) return;
          const current = Array.isArray(subjectLock.uploaded_urls) && subjectLock.uploaded_urls.length
            ? subjectLock.uploaded_urls
            : (subjectLock.uploaded_url ? [subjectLock.uploaded_url] : []);
          const remaining = current.filter((u) => u !== urlToRemove);
          if (remaining.length === 0) {
            clearSubjectLock();
            return;
          }
          setSubjectLock({
            ...subjectLock,
            uploaded_url: remaining[0],
            uploaded_urls: remaining,
            url: remaining.includes(subjectLock.url) ? subjectLock.url : remaining[0],
            prompt: remaining.length > 1
              ? `${remaining.length} subjects locked`
              : subjectLock.prompt,
            locked_at: new Date().toISOString(),
          });
          toast.success(
            remaining.length > 1
              ? `Subject removed — ${remaining.length} still locked.`
              : "Subject removed — 1 still locked."
          );
        }}
        onToggle={(nextEnabled) => {
          if (!subjectLock) return;
          setSubjectLock({ ...subjectLock, enabled: !!nextEnabled });
          toast(nextEnabled
            ? "Subject lock ON — carrying identity."
            : "Subject lock paused — kept attached, not sent."
          );
        }}
      />

      {/* Persistent Cast Chip — visible on every mode when the library has
          at least one character. Click to expand a popover with all pinned
          characters; click any avatar to switch active; X to remove. */}
      {pinnedCharacter?.front_url && (
        <div
          data-testid="cast-chip"
          className="fixed z-50 pointer-events-none"
          style={{ bottom: "calc(232px + env(safe-area-inset-bottom))", left: "50%", transform: "translateX(-50%)" }}
        >
          <div className="pointer-events-auto relative">
            <button
              onClick={() => setCastMenuOpen((v) => !v)}
              data-testid="cast-chip-toggle"
              className="inline-flex items-center gap-2 pl-1 pr-3 py-1 rounded-full border shadow-[0_0_28px_-8px_rgba(242,201,76,0.6)] hover:shadow-[0_0_36px_-6px_rgba(242,201,76,0.85)] transition-all"
              style={{
                background: "linear-gradient(90deg, rgba(88,28,135,0.35) 0%, rgba(18,14,25,0.85) 55%, rgba(0,229,255,0.15) 100%)",
                borderColor: ensembleCharacters.length > 0 ? "rgba(0,229,255,0.6)" : "rgba(242,201,76,0.35)",
                backdropFilter: "blur(8px)",
              }}
            >
              <div className="relative flex items-center">
                <img
                  src={pinnedCharacter.front_url}
                  alt="Cast"
                  className="w-6 h-6 rounded-full object-cover border border-[#F2C94C]/60"
                  data-testid="cast-chip-thumb"
                />
                {ensembleCharacters.map((c, idx) => (
                  <img
                    key={c.character_generation_id}
                    src={c.front_url}
                    alt={`Ensemble partner ${idx + 1}`}
                    className="w-6 h-6 rounded-full object-cover border border-[#00E5FF] -ml-2 shadow-[0_0_10px_-2px_rgba(0,229,255,0.9)]"
                    data-testid={idx === 0 ? "cast-chip-ensemble-thumb" : `cast-chip-ensemble-thumb-${idx}`}
                  />
                ))}
              </div>
              <span
                className="font-mono-hy text-[9px] tracking-[0.22em] uppercase"
                style={{ color: ensembleCharacters.length > 0 ? "#00E5FF" : "#F2C94C" }}
              >
                {ensembleCharacters.length === 0 && `CAST · ${pinnedCharacter.name || "PINNED"}`}
                {ensembleCharacters.length === 1 && `ENSEMBLE · ${pinnedCharacter.name || "LEAD"} + ${ensembleCharacters[0].name || "PARTNER"}`}
                {ensembleCharacters.length === 2 && `TRIO · ${pinnedCharacter.name || "LEAD"} + ${ensembleCharacters[0].name || "A"} + ${ensembleCharacters[1].name || "B"}`}
              </span>
              {ensembleCharacters.length === 0 && castLibrary.length > 1 && (
                <span className="font-mono-hy text-[8px] text-white/70 tracking-widest px-1.5 py-0.5 rounded-full bg-white/[0.06] border border-white/[0.1]">
                  {castLibrary.length}
                </span>
              )}
              <ChevronDown className={`w-3 h-3 text-white/60 transition-transform ${castMenuOpen ? "rotate-180" : ""}`} />
            </button>

            {castMenuOpen && (
              <div
                data-testid="cast-menu"
                className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 w-[320px] rounded-2xl border p-3 shadow-2xl"
                style={{
                  background: "linear-gradient(160deg, #120E1F 0%, #0A0812 100%)",
                  borderColor: "rgba(242,201,76,0.25)",
                  backdropFilter: "blur(10px)",
                }}
              >
                <div className="flex items-center justify-between mb-2 px-1">
                  <span className="font-mono-hy text-[9px] text-[#F2C94C] tracking-[0.22em]">
                    CAST LIBRARY · {castLibrary.length}/5
                  </span>
                  {castLibrary.length > 1 && (
                    <button
                      onClick={clearAllCast}
                      data-testid="cast-menu-clear-all"
                      className="font-mono-hy text-[9px] text-white/60 hover:text-[#EB5757] tracking-widest transition-colors"
                    >
                      CLEAR ALL
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-5 gap-2">
                  {castLibrary.map((c, i) => {
                    const isActive = i === 0;
                    const isEnsemble = !isActive && ensembleIds.includes(c.character_generation_id);
                    return (
                      <div
                        key={c.character_generation_id}
                        data-testid={`cast-slot-${i}`}
                        className="relative aspect-square rounded-xl overflow-hidden border-2 group"
                        style={{
                          borderColor: isActive ? "#F2C94C" : (isEnsemble ? "#00E5FF" : "rgba(255,255,255,0.08)"),
                          boxShadow: isActive
                            ? "0 0 24px -6px rgba(242,201,76,0.7)"
                            : (isEnsemble ? "0 0 24px -6px rgba(0,229,255,0.75)" : "none"),
                        }}
                      >
                        <button
                          onClick={() => activateCastMember(c.character_generation_id)}
                          data-testid={`cast-slot-${i}-activate`}
                          className="absolute inset-0"
                          title={c.name || "Set as active"}
                        >
                          <img src={c.front_url} alt={c.name || "cast"} className="w-full h-full object-cover" />
                        </button>
                        {isActive && (
                          <div className="absolute top-1 left-1 w-3.5 h-3.5 rounded-full bg-[#F2C94C] flex items-center justify-center">
                            <Check className="w-2.5 h-2.5 text-[#0A0A0A]" strokeWidth={3} />
                          </div>
                        )}
                        {isEnsemble && (
                          <div className="absolute top-1 left-1 w-3.5 h-3.5 rounded-full bg-[#00E5FF] flex items-center justify-center shadow-[0_0_10px_-2px_rgba(0,229,255,0.9)]">
                            <UsersRound className="w-2.5 h-2.5 text-[#0A0A0A]" strokeWidth={3} />
                          </div>
                        )}
                        {/* Ensemble toggle — only on non-active slots */}
                        {!isActive && (
                          <button
                            onClick={(e) => { e.stopPropagation(); toggleEnsemble(c.character_generation_id); }}
                            data-testid={`cast-slot-${i}-ensemble`}
                            aria-label={isEnsemble ? "Remove from ensemble" : "Add to ensemble"}
                            title={isEnsemble ? "Remove from ensemble" : "Pair as ensemble co-star"}
                            className={`absolute bottom-1 left-1 w-4 h-4 rounded-full flex items-center justify-center transition-all ${
                              isEnsemble
                                ? "bg-[#00E5FF] border border-[#00E5FF] opacity-100"
                                : "bg-[#0A0A0A]/85 border border-white/25 hover:border-[#00E5FF] hover:bg-[#00E5FF]/25 opacity-70 group-hover:opacity-100"
                            }`}
                          >
                            <UsersRound className={`w-2.5 h-2.5 ${isEnsemble ? "text-[#0A0A0A]" : "text-white"}`} strokeWidth={2.5} />
                          </button>
                        )}
                        <button
                          onClick={(e) => { e.stopPropagation(); removeCastMember(c.character_generation_id); }}
                          data-testid={`cast-slot-${i}-remove`}
                          aria-label="Remove"
                          className="absolute top-1 right-1 w-4 h-4 rounded-full bg-[#0A0A0A]/85 border border-white/20 hover:border-[#EB5757] hover:bg-[#EB5757] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-2.5 h-2.5 text-white" />
                        </button>
                        {c.name && (
                          <div className="absolute bottom-0 left-0 right-0 px-1 py-0.5 font-mono-hy text-[8px] text-white truncate bg-gradient-to-t from-[#0A0A0A] to-transparent">
                            {c.name}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {/* Empty slots */}
                  {Array.from({ length: Math.max(0, 5 - castLibrary.length) }).map((_, i) => (
                    <div
                      key={`empty-${i}`}
                      className="aspect-square rounded-xl border-2 border-dashed border-white/[0.08] flex items-center justify-center"
                      title="Empty slot — open a Character Sheet to add"
                    >
                      <Users className="w-3.5 h-3.5 text-white/20" />
                    </div>
                  ))}
                </div>
                {/* Ensemble / Trio banner */}
                {ensembleCharacters.length > 0 ? (
                  <div
                    data-testid="ensemble-banner"
                    className="mt-3 px-2.5 py-2 rounded-xl border flex items-center justify-between gap-2"
                    style={{
                      background: "linear-gradient(90deg, rgba(0,229,255,0.10) 0%, rgba(242,201,76,0.08) 100%)",
                      borderColor: "rgba(0,229,255,0.35)",
                    }}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <UsersRound className="w-3.5 h-3.5 text-[#00E5FF] shrink-0" />
                      <span className="font-mono-hy text-[9px] text-[#00E5FF] tracking-[0.18em] truncate">
                        {ensembleCharacters.length === 1 ? "ENSEMBLE" : "TRIO"} · {[pinnedCharacter?.name || "LEAD", ...ensembleCharacters.map((c) => c.name || "CO-STAR")].join(" + ")}
                      </span>
                    </div>
                    <button
                      onClick={clearEnsemble}
                      data-testid="ensemble-clear"
                      className="font-mono-hy text-[9px] text-white/60 hover:text-[#EB5757] tracking-widest transition-colors shrink-0"
                    >
                      CLEAR
                    </button>
                  </div>
                ) : (
                  castLibrary.length > 1 && (
                    <p className="mt-3 px-1 font-mono-hy text-[9px] text-[#00E5FF]/70 leading-relaxed tracking-wide flex items-center gap-1.5">
                      <UsersRound className="w-3 h-3" />
                      Tap the duo icon on up to two co-stars to pair a duo or trio for a shared scene.
                    </p>
                  )
                )}
                <p className="mt-2 px-1 font-mono-hy text-[9px] text-white/60 leading-relaxed tracking-wide">
                  Tap any character to make them the active cast. The active member's front view is auto-attached to every new prompt.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── FIXED-BOTTOM CHAT PROMPT DOCK ───────────────────────────────── */}
      <ChatPromptDock
        queueSlot={
          <QueueProgressPill
            variant="inline"
            items={feed.items}
            onClick={() => {
              const el = document.querySelector('[data-testid^="feed-item-"]');
              if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
            }}
          />
        }
        mode={dockMode}
        setMode={(m) => {
          setDockMode(m);
          // Keep the classic-editor mode in sync so advanced controls follow
          if (m === "image")      switchMode("create");
          else if (m === "video") switchMode("text-video");
          else if (m === "v2v")   switchMode("video-to-video");
          else if (m === "turnaround") switchMode("turnaround");
        }}
        videoSubmode={videoSubmode}
        setVideoSubmode={(s) => {
          setVideoSubmode(s);
          // "Smart Edit" behaves like V2V — flip mode accordingly for parity with the queue endpoint
          if (s === "smartedit") { setDockMode("v2v"); switchMode("video-to-video"); }
          else if (dockMode === "v2v" && s !== "smartedit") { setDockMode("video"); switchMode("text-video"); }
        }}
        videoModel={videoModel}
        setVideoModel={setVideoModel}
        musicEnabled={musicEnabled}
        setMusicEnabled={setMusicEnabled}
        aspect={aspect}
        setAspect={setAspect}
        duration={duration}
        setDuration={setDuration}
        prompt={prompt}
        setPrompt={setPrompt}
        onSubmit={handleDockSubmit}
        auto={autoOn}
        setAuto={setAutoOn}
        onAngleChange={(preset) => setActiveAngle(preset)}
        onCameraChange={(preset) => setActiveCameraMove(preset)}
        onFeedDrop={(feedItem) => attachFeedItem(feedItem)}
        // Left-side thumbnail rail — primary attach zone. Feeds off the same
        // omniRefs state as the paperclip popover so both entry points stay
        // in sync. Capped at 5 per the redesign spec.
        refs={omniRefs}
        onAddRef={addOmniRef}
        onRemoveRef={removeOmniRef}
        onReorderRefs={reorderOmniRef}
        onReplaceRef={replaceOmniRef}
        refsCap={5}
        placeholderByMode={{
          image: "A regal Nubian starlord in obsidian armor, crown of gold hieroglyphs…",
          video: "A regal Nubian starlord walking through a cosmic temple, dust motes glinting…",
          v2v:   "Restyle to cel-shaded 2D African anime with dramatic speed lines…",
          turnaround: "A vibranium-clad Wakandan warrior with a spear, cobalt cape, gold shoulder plates…",
        }}
        attachSummary={
          <>
            {subjectLock && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#00E5FF]/12 border border-[#00E5FF]/45 font-mono-hy text-[9px] text-[#00E5FF] tracking-[0.15em] shadow-[0_0_18px_-4px_rgba(0,229,255,0.55)]" data-testid="dock-subject-chip">
                🔒 SUBJECT LOCKED
              </span>
            )}
            {dockMode === "video" && videoSubmode === "multiframe" && multiframes.some(Boolean) && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#00E5FF]/10 border border-[#00E5FF]/40 font-mono-hy text-[9px] text-[#00E5FF] tracking-[0.15em]" data-testid="dock-multiframe-chip">
                <LayoutGrid className="w-2.5 h-2.5" /> {multiframes.filter(Boolean).length}/4 KEYFRAMES
              </span>
            )}
            {omniRefs.length > 0 && !(dockMode === "video" && videoSubmode === "multiframe") && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#00E5FF]/8 border border-[#00E5FF]/30 font-mono-hy text-[9px] text-[#00E5FF] tracking-[0.15em]">
                <LayoutGrid className="w-2.5 h-2.5" /> {omniRefs.length} REF{omniRefs.length > 1 ? "S" : ""}
              </span>
            )}
            {(startFrame || endFrame) && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#00E5FF]/8 border border-[#00E5FF]/30 font-mono-hy text-[9px] text-[#00E5FF] tracking-[0.15em]">
                <Film className="w-2.5 h-2.5" /> {[startFrame && "start", endFrame && "end"].filter(Boolean).join(" · ")}
              </span>
            )}
            {videoPreview?.uploaded_url && dockMode === "v2v" && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#EB5757]/8 border border-[#EB5757]/30 font-mono-hy text-[9px] text-[#EB5757] tracking-[0.15em]">
                <Film className="w-2.5 h-2.5" /> SOURCE CLIP LOCKED
              </span>
            )}
            {autoOn && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#F2C94C]/8 border border-[#F2C94C]/30 font-mono-hy text-[9px] text-[#F2C94C] tracking-[0.15em]" data-testid="dock-active-pill">
                <Zap className="w-2.5 h-2.5" /> AUTO ON · {aspect}
                {stylesArr.length === 1 && <span>· {styleLabel(stylesArr[0])}</span>}
                {stylesArr.length >= 2 && (
                  <span className="text-[#F2C94C]" data-testid="dock-multi-styles-pill">
                    · {stylesArr.length} STYLES BLENDED
                  </span>
                )}
                {stylesArr.length === 0 && <span className="text-white">· NO STYLE</span>}
                {dockMode === "image" && activeAngle && (
                  <span className="text-[#00E5FF] uppercase tracking-[0.14em]" data-testid="dock-active-angle">
                    · {activeAngle.name}
                  </span>
                )}
                {dockMode !== "image" && activeCameraMove && (
                  <span className="text-[#F2C94C] uppercase tracking-[0.14em]" data-testid="dock-active-cameramove">
                    · {activeCameraMove.name}
                  </span>
                )}
                <span className="text-white mx-1">·</span>
                <span className="text-[#00E5FF]">{dockMode === "video" || dockMode === "v2v" ? "SEEDANCE" : "NANO·BANANA"}</span>
              </span>
            )}
          </>
        }
        attachContent={
          <div className="space-y-4">
            {/* Multiframes submode swaps the standard pickers for a 4-slot keyframe rail */}
            {dockMode === "video" && videoSubmode === "multiframe" ? (
              <MultiframeRail
                slots={multiframes}
                setSlots={setMultiframes}
                style={style}
              />
            ) : dockMode === "image" ? (
              /* Image mode — 5 generic unlabeled reference slots.
                 Omni Reference card is intentionally removed from this dropdown per
                 the studio-simplification plan; slots are unordered and equal-weight. */
              <ImageRefSlots
                refs={omniRefs.slice(0, 5)}
                onPick={addOmniRef}
                onRemove={removeOmniRef}
                onDropFeed={(feedItem) => attachFeedItem(feedItem)}
                max={5}
                testId="image-ref-slots-dock"
              />
            ) : (
              <>
                {/* Video / animation / turnaround — Omni card only surfaces when
                    the Omni video submode is active (Omni is a video-first feature). */}
                {dockMode === "video" && videoSubmode === "omni" && (
                  <OmniReferenceCard
                    mode="create"
                    omniRefs={omniRefs}
                    addOmniRef={addOmniRef}
                    removeOmniRef={removeOmniRef}
                    setOmniRefs={setOmniRefs}
                    testIdSuffix="dock"
                  />
                )}
                {(dockMode === "video" || dockMode === "turnaround") && (
                  <StartEndFramePicker
                    startFrame={startFrame}
                    endFrame={endFrame}
                    onChangeStart={setStartFrame}
                    onChangeEnd={setEndFrame}
                    testIdPrefix="frame-dock"
                  />
                )}
              </>
            )}
            {dockMode === "v2v" && (
              <div className="p-4 rounded-2xl bg-[#0A0A0A]/60 border border-[#EB5757]/30">
                <div className="flex items-center justify-between mb-2">
                  <label className="font-mono-hy text-[10px] text-[#EB5757] flex items-center gap-1.5">
                    <Film className="w-3 h-3" /> Source Clip
                  </label>
                  {videoPreview && (
                    <button onClick={() => { setVideoPreview(null); if (videoRef.current) videoRef.current.value = ""; }}
                      data-testid="dock-v2v-clear"
                      className="font-mono-hy text-[9px] text-[#E27D60] hover:text-white">Clear</button>
                  )}
                </div>
                <input ref={videoRef} type="file" accept="video/*" onChange={(e) => handleVideoFile(e.target.files?.[0])} className="hidden" data-testid="dock-v2v-file-input" />
                {videoPreview ? (
                  <video src={videoPreview.url} data-testid="dock-v2v-preview" className="w-full h-32 object-cover rounded-xl border border-[#EB5757]/30 bg-black" muted playsInline />
                ) : (
                  <button onClick={() => videoRef.current?.click()} data-testid="dock-v2v-pick"
                    className="w-full h-20 rounded-xl border-2 border-dashed border-[#EB5757]/30 hover:border-[#EB5757] hover:bg-[#EB5757]/5 transition-all flex flex-col items-center justify-center gap-1">
                    <Upload className="w-4 h-4 text-[#EB5757]" />
                    <span className="font-mono-hy text-[10px] text-white">Drop a 3-10s clip · max 40MB</span>
                  </button>
                )}
              </div>
            )}
          </div>
        }
        skillsContent={
          <div className="space-y-4">
            {/* ── Selected Styles summary — equal-weight multi-blend ─────── */}
            <div className="p-4 rounded-2xl bg-[#0A0A0A]/60 border border-[#F2C94C]/25" data-testid="skills-selected-section">
              <div className="flex items-center justify-between mb-3">
                <div className="font-mono-hy text-[9px] text-[#F2C94C] tracking-[0.24em] flex items-center gap-1.5">
                  <Sparkles className="w-3 h-3" />
                  {stylesArr.length === 0
                    ? "NO STYLES SELECTED"
                    : stylesArr.length === 1
                    ? "1 STYLE APPLIED"
                    : `${stylesArr.length} STYLES · EQUAL BLEND`}
                </div>
                {stylesArr.length > 0 && (
                  <button
                    onClick={clearStyles}
                    data-testid="skills-styles-clear"
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-white/[0.08] bg-white/[0.02] text-white hover:border-[#EB5757]/50 hover:text-[#EB5757] font-mono-hy text-[9px] tracking-[0.18em] transition-all"
                  >
                    CLEAR ALL
                  </button>
                )}
              </div>

              {stylesArr.length === 0 ? (
                <div className="rounded-xl border-2 border-dashed border-[#F2C94C]/20 bg-[#F2C94C]/[0.02] px-3 py-4 flex flex-col items-center justify-center gap-1" data-testid="skills-styles-empty">
                  <Sparkles className="w-4 h-4 text-[#F2C94C]/50" />
                  <span className="font-mono-hy text-[9px] text-white tracking-widest text-center">
                    Pick any spell cards below — Geneo blends them equally.
                  </span>
                </div>
              ) : (
                <div className="flex items-start gap-3" data-testid="skills-preview-row">
                  <StyleBlendPreview ids={stylesArr} size={88} testId="skills-blend-preview" />
                  <div className="flex flex-wrap gap-1.5 flex-1" data-testid="skills-selected-pills">
                    {stylesArr.map((id) => {
                      const s = STYLES.find((x) => x.id === id);
                      if (!s) return null;
                      return (
                        <button
                          key={id}
                          onClick={() => toggleStyle(id)}
                          data-testid={`skills-selected-pill-${id}`}
                          title={`Remove ${s.name}`}
                          className="group inline-flex items-center gap-1.5 pl-1 pr-2 py-0.5 rounded-full border border-[#F2C94C]/40 bg-[#F2C94C]/[0.08] text-[#F2C94C] font-mono-hy text-[9px] tracking-[0.12em] hover:bg-[#EB5757]/10 hover:border-[#EB5757]/50 hover:text-[#EB5757] transition-all self-start"
                        >
                          <img src={s.thumb} alt="" className="w-4 h-4 rounded-full object-cover border border-white/10" />
                          <span>{s.name}</span>
                          <X className="w-2.5 h-2.5 opacity-60 group-hover:opacity-100" />
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              <p className="font-mono-hy text-[9px] text-white mt-3 leading-relaxed">
                {stylesArr.length >= 2
                  ? "All selected styles blend equally into every image, video, and turnaround — no single one dominates."
                  : "Tap spell cards below to add or remove. Leave empty for verbatim prompt fidelity."}
              </p>
            </div>

            <div>
              <div className="font-mono-hy text-[9px] text-[#F2C94C] tracking-[0.24em] mb-2">STYLE · SPELL CARDS</div>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-[52vh] overflow-y-auto pr-1">
                {STYLES.map((s) => (
                  <StylePresetChip
                    key={s.id}
                    preset={s}
                    active={stylesArr.includes(s.id)}
                    onClick={() => toggleStyle(s.id)}
                    size="sm"
                    testId={`dock-style-${s.id}`}
                  />
                ))}
              </div>
              <div className="font-mono-hy text-[9px] text-white mt-2 leading-relaxed">
                {stylesArr.length === 0
                  ? "No styles locked · Geneo will follow your prompt verbatim."
                  : stylesArr.length === 1
                  ? "Tap the active card again to remove it. Add more to blend styles equally."
                  : `${stylesArr.length} styles blended equally — tap any active card to remove it.`}
              </div>
            </div>
            <div>
              <div className="font-mono-hy text-[9px] text-[#00E5FF] tracking-[0.24em] mb-2">ASPECT RATIO</div>
              <div className="flex flex-wrap gap-1.5">
                {(dockMode === "video" || dockMode === "v2v" ? ["16:9", "9:16", "1:1"] : ["1:1", "3:4", "4:3", "16:9", "9:16"]).map((a) => (
                  <button
                    key={a}
                    onClick={() => setAspect(a)}
                    data-testid={`dock-aspect-${a.replace(":", "-")}`}
                    className={`px-3 py-1.5 rounded-full border font-mono-hy text-[9px] tracking-widest transition-all ${
                      aspect === a
                        ? "border-[#00E5FF] bg-[#00E5FF]/10 text-[#00E5FF] shadow-[0_0_16px_-4px_rgba(0,229,255,0.6)]"
                        : "border-white/[0.08] bg-white/[0.02] text-white hover:border-[#00E5FF]/50"
                    }`}
                  >
                    {a}
                  </button>
                ))}
              </div>
            </div>
            {(dockMode === "video" || dockMode === "v2v") && (
              <div>
                <div className="font-mono-hy text-[9px] text-[#00E5FF] tracking-[0.24em] mb-2">VIDEO RESOLUTION</div>
                <div className="flex gap-1.5">
                  {["480p", "720p", "1080p"].map((r) => (
                    <button
                      key={r}
                      onClick={() => setVideoResolution(r)}
                      data-testid={`dock-vres-${r}`}
                      className={`px-3 py-1.5 rounded-full border font-mono-hy text-[9px] tracking-widest transition-all ${
                        videoResolution === r
                          ? "border-[#00E5FF] bg-[#00E5FF]/10 text-[#00E5FF]"
                          : "border-white/[0.08] bg-white/[0.02] text-white hover:border-[#00E5FF]/50"
                      }`}
                    >
                      {r}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        }
      />

      {/* Voice Clone Modal */}
      <VoiceCloneModal
        open={voiceCloneOpen}
        onClose={() => setVoiceCloneOpen(false)}
        busy={voiceCloning}
        name={voiceCloneName}
        onNameChange={setVoiceCloneName}
        onSubmit={submitVoiceClone}
        fileRef={voiceSampleRef}
      />
    </div>
  );
}
